import { StrictMode, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  authenticateLocalUser,
  clearAuthSession,
  formatMobile,
  getAuthSession,
  getCurrentLocalUser,
  getEmailVerification,
  getPendingSignup,
  isDuplicateLoginId,
  isValidEmail,
  isValidMobile,
  isValidPassword,
  issueLocalEmailVerification,
  registerPendingUser,
  savePendingSignup,
  updateCurrentLocalUser,
  type PendingSignup,
  type SignupGender
} from "./browser-auth";
import {
  createLocalInquiry,
  createLocalModelRequest,
  createLocalPayment,
  getCurrentLocalPayment,
  getLocalCreations,
  getLocalInquiries,
  getLocalModelRequests,
  getLocalPayments,
  getPendingLocalPayment,
  getSelectedLocalInquiry,
  getSelectedLocalModelRequest,
  getSelectedLocalPayment,
  selectLocalInquiry,
  selectLocalModelRequest,
  selectLocalPayment,
  upsertLocalCreation,
  type LocalModelRequest,
  type LocalPayment
} from "./browser-data";
import "../styles.css";

type Route =
  | "splash"
  | "login"
  | "signup-terms"
  | "signup-profile"
  | "signup-verify-email"
  | "find-id-start"
  | "find-id-verify"
  | "find-id-result"
  | "reset-password-start"
  | "reset-password-verify"
  | "reset-password-form"
  | "reset-password-complete"
  | "home"
  | "plans"
  | "checkout"
  | "library"
  | "models"
  | "shop-images"
  | "shop-portfolio"
  | "shop-apply"
  | "ai-model"
  | "ai-type"
  | "ai-product"
  | "ai-options"
  | "ai-loading"
  | "ai-result"
  | "ai-download"
  | "my"
  | "my-profile"
  | "my-subscription"
  | "my-requests"
  | "my-request-detail"
  | "my-payments"
  | "my-payment-detail"
  | "my-inquiries"
  | "my-inquiry-new"
  | "my-inquiry-detail";

const routeByHash: Record<string, Route> = {
  "#/splash": "splash",
  "#/login": "login",
  "#/signup/terms": "signup-terms",
  "#/signup/profile": "signup-profile",
  "#/signup/verify-email": "signup-verify-email",
  "#/account/find-id": "find-id-start",
  "#/account/find-id/verify": "find-id-verify",
  "#/account/find-id/result": "find-id-result",
  "#/account/reset-password": "reset-password-start",
  "#/account/reset-password/verify": "reset-password-verify",
  "#/account/reset-password/new": "reset-password-form",
  "#/account/reset-password/complete": "reset-password-complete",
  "#/home": "home",
  "#/plans": "plans",
  "#/checkout": "checkout",
  "#/library": "library",
  "#/models": "models",
  "#/models/shop/images": "shop-images",
  "#/models/shop/portfolio": "shop-portfolio",
  "#/models/shop/apply": "shop-apply",
  "#/ai-model": "ai-model",
  "#/ai-model/type": "ai-type",
  "#/ai-model/product": "ai-product",
  "#/ai-model/options": "ai-options",
  "#/ai-model/loading": "ai-loading",
  "#/ai-model/result": "ai-result",
  "#/ai-model/download": "ai-download",
  "#/my": "my",
  "#/my/profile": "my-profile",
  "#/my/subscription": "my-subscription",
  "#/my/requests": "my-requests",
  "#/my/requests/detail": "my-request-detail",
  "#/my/payments": "my-payments",
  "#/my/payments/detail": "my-payment-detail",
  "#/my/inquiries": "my-inquiries",
  "#/my/inquiries/new": "my-inquiry-new",
  "#/my/inquiries/detail": "my-inquiry-detail"
};

const splashSeenKey = "art4u:splash-seen";

function currentRoute(): Route {
  return routeByHash[window.location.hash] ?? "splash";
}

function navigate(hash: keyof typeof routeByHash) {
  if (window.location.hash === hash) {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    return;
  }
  window.location.hash = hash;
}

function SplashScreen() {
  useEffect(() => {
    window.sessionStorage.setItem(splashSeenKey, "true");
    const timer = window.setTimeout(() => navigate("#/login"), 3000);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <main className="mobile-canvas splash-screen" aria-label="ART4U 시작 화면">
      <img className="splash-symbol" src="/assets/art4u/symbol.svg" alt="" />
      <div className="splash-copy">
        <img className="splash-logo" src="/assets/art4u/logo-light.svg" alt="ART4U" />
        <h1>AI 광고 모델 제작 플랫폼</h1>
        <p>사진 한 장으로 시작하는 손쉬운 콘텐츠 제작</p>
      </div>
    </main>
  );
}

function LoginScreen() {
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!loginId.trim() || !password) {
      setMessage("아이디와 비밀번호를 모두 입력해주세요.");
      return;
    }
    setSubmitting(true);
    try {
      const session = await authenticateLocalUser(loginId, password);
      if (!session) {
        setMessage("아이디 또는 비밀번호가 일치하지 않습니다.");
        return;
      }
      setMessage("");
      navigate("#/home");
    } catch {
      setMessage("브라우저 세션을 사용할 수 없습니다.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mobile-canvas login-screen">
      <section className="login-content" aria-labelledby="login-title">
        <h1 id="login-title" className="visually-hidden">로그인</h1>
        <img className="login-logo" src="/assets/art4u/logo-dark.svg" alt="ART4U" />
        <form className="login-form" onSubmit={submit} noValidate>
          <label className="visually-hidden" htmlFor="login-id">아이디</label>
          <input
            id="login-id"
            name="loginId"
            autoComplete="username"
            value={loginId}
            onChange={(event) => setLoginId(event.target.value)}
            placeholder="아이디를 입력해주세요."
          />
          <label className="visually-hidden" htmlFor="login-password">비밀번호</label>
          <input
            id="login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="비밀번호를 입력해주세요."
          />
          <button className="button button-primary login-submit" type="submit" disabled={submitting}>
            {submitting ? "로그인 중..." : "로그인"}
          </button>
          <p className={message ? "form-message" : "form-message is-empty"} role="status">{message || "안내"}</p>
        </form>

        <div className="account-links" aria-label="계정 찾기">
          <button type="button" onClick={() => navigate("#/account/find-id")}>ID 찾기</button>
          <span aria-hidden="true" />
          <button type="button" onClick={() => navigate("#/account/reset-password")}>PW 찾기</button>
        </div>
        <button className="button button-secondary signup-button" type="button" onClick={() => navigate("#/signup/terms")}>회원가입</button>
      </section>
    </main>
  );
}

interface Agreement {
  id: "service" | "privacy" | "marketing";
  label: string;
  required: boolean;
}

const agreements: Agreement[] = [
  { id: "service", label: "서비스 이용약관 동의", required: true },
  { id: "privacy", label: "개인정보 수집 및 이용 동의", required: true },
  { id: "marketing", label: "마케팅 정보 수신 동의", required: false }
];

function AuthHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <header className="auth-header">
      <button className="header-icon" type="button" aria-label="이전 화면" onClick={onBack}>←</button>
      <strong>{title}</strong>
      <span className="header-icon" aria-hidden="true" />
    </header>
  );
}

function CheckMark({ checked }: { checked: boolean }) {
  return <span className={`check-mark${checked ? " is-checked" : ""}`} aria-hidden="true">{checked ? "✓" : ""}</span>;
}

function TermsScreen() {
  const [checked, setChecked] = useState<Record<Agreement["id"], boolean>>({ service: false, privacy: false, marketing: false });
  const [open, setOpen] = useState<Agreement["id"] | null>(null);
  const allChecked = agreements.every((item) => checked[item.id]);
  const canContinue = agreements.filter((item) => item.required).every((item) => checked[item.id]);

  function toggleAll() {
    const next = !allChecked;
    setChecked({ service: next, privacy: next, marketing: next });
  }

  return (
    <main className="mobile-canvas auth-flow-screen">
      <AuthHeader title="회원가입" onBack={() => navigate("#/login")} />
      <section className="auth-flow-content terms-content">
        <h1>서비스 이용을 위해<br />약관에 동의해주세요.</h1>

        <button className="agreement-all" type="button" onClick={toggleAll} aria-pressed={allChecked}>
          <CheckMark checked={allChecked} />
          <strong>전체 동의</strong>
        </button>

        <div className="agreement-list">
          {agreements.map((agreement) => (
            <article className="agreement-item" key={agreement.id}>
              <div className="agreement-row">
                <button
                  className="agreement-toggle"
                  type="button"
                  onClick={() => setChecked((value) => ({ ...value, [agreement.id]: !value[agreement.id] }))}
                  aria-pressed={checked[agreement.id]}
                >
                  <CheckMark checked={checked[agreement.id]} />
                  <span><em>[{agreement.required ? "필수" : "선택"}]</em> {agreement.label}</span>
                </button>
                <button
                  className="agreement-detail-toggle"
                  type="button"
                  aria-label={`${agreement.label} 내용 ${open === agreement.id ? "닫기" : "보기"}`}
                  aria-expanded={open === agreement.id}
                  onClick={() => setOpen((value) => value === agreement.id ? null : agreement.id)}
                >›</button>
              </div>
              {open === agreement.id ? (
                <p className="agreement-preview">약관 원문은 운영 정책 확정 후 연결됩니다.</p>
              ) : null}
            </article>
          ))}
        </div>
      </section>
      <div className="sticky-action">
        <button className="button button-primary" type="button" disabled={!canContinue} onClick={() => navigate("#/signup/profile")}>다음</button>
      </div>
    </main>
  );
}

function Field({ label, required = true, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="field">
      <span className="field-label">{label}{required ? <em> *</em> : null}</span>
      {children}
    </div>
  );
}

type SignupFormState = Omit<PendingSignup, "gender"> & {
  gender: SignupGender | "";
  passwordConfirm: string;
};

type SignupFieldName = keyof SignupFormState | "idCheck";

function ProfileScreen() {
  const [form, setForm] = useState<SignupFormState>({
    loginId: "",
    password: "",
    passwordConfirm: "",
    name: "",
    birthDate: "",
    gender: "",
    mobile: "",
    email: ""
  });
  const [errors, setErrors] = useState<Partial<Record<SignupFieldName, string>>>({});
  const [checkedId, setCheckedId] = useState("");
  const [idNotice, setIdNotice] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
  const today = new Date().toISOString().slice(0, 10);

  function updateForm<Key extends keyof SignupFormState>(key: Key, value: SignupFormState[Key]) {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  }

  function checkDuplicateId() {
    const loginId = form.loginId.trim();
    if (!loginId) {
      setCheckedId("");
      setIdNotice({ tone: "error", text: "아이디를 입력해주세요." });
      return;
    }
    if (isDuplicateLoginId(loginId)) {
      setCheckedId("");
      setIdNotice({ tone: "error", text: "이미 사용 중인 아이디입니다." });
      return;
    }
    setCheckedId(loginId);
    setIdNotice({ tone: "ok", text: "사용 가능한 아이디입니다." });
    setErrors((current) => ({ ...current, loginId: undefined, idCheck: undefined }));
  }

  function validate() {
    const nextErrors: Partial<Record<SignupFieldName, string>> = {};
    if (!form.loginId.trim()) nextErrors.loginId = "아이디를 입력해주세요.";
    else if (checkedId !== form.loginId.trim()) nextErrors.idCheck = "아이디 중복확인을 완료해주세요.";
    if (!form.password) nextErrors.password = "비밀번호를 입력해주세요.";
    else if (!isValidPassword(form.password)) nextErrors.password = "영문과 숫자를 포함한 8~20자로 입력해주세요.";
    if (!form.passwordConfirm) nextErrors.passwordConfirm = "비밀번호 확인을 입력해주세요.";
    else if (form.password !== form.passwordConfirm) nextErrors.passwordConfirm = "비밀번호가 일치하지 않습니다.";
    if (!form.name.trim()) nextErrors.name = "이름을 입력해주세요.";
    if (!form.birthDate) nextErrors.birthDate = "생년월일을 선택해주세요.";
    else if (form.birthDate > today) nextErrors.birthDate = "오늘 이후의 날짜는 선택할 수 없습니다.";
    if (!form.gender) nextErrors.gender = "성별을 선택해주세요.";
    if (!form.mobile) nextErrors.mobile = "휴대폰 번호를 입력해주세요.";
    else if (!isValidMobile(form.mobile)) nextErrors.mobile = "올바른 휴대폰 번호를 입력해주세요.";
    if (!form.email.trim()) nextErrors.email = "이메일을 입력해주세요.";
    else if (!isValidEmail(form.email)) nextErrors.email = "올바른 이메일 형식으로 입력해주세요.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!validate() || !form.gender) return;
    const signup: PendingSignup = {
      loginId: form.loginId.trim(),
      password: form.password,
      name: form.name.trim(),
      birthDate: form.birthDate,
      gender: form.gender,
      mobile: form.mobile,
      email: form.email.trim()
    };
    try {
      savePendingSignup(signup);
      issueLocalEmailVerification(signup.email);
      navigate("#/signup/verify-email");
    } catch {
      setErrors({ idCheck: "브라우저 세션 저장소를 사용할 수 없습니다." });
    }
  }

  return (
    <main className="mobile-canvas auth-flow-screen profile-screen">
      <AuthHeader title="회원가입" onBack={() => navigate("#/signup/terms")} />
      <form id="signup-profile-form" className="auth-flow-content profile-content" onSubmit={submit} noValidate>
        <h1>기본정보를<br />입력해주세요.</h1>
        <div className="field-stack">
          <Field label="아이디">
            <div className="inline-field">
              <input
                className={errors.loginId || errors.idCheck ? "has-error" : ""}
                name="loginId"
                autoComplete="username"
                value={form.loginId}
                onChange={(event) => {
                  updateForm("loginId", event.target.value);
                  setCheckedId("");
                  setIdNotice(null);
                  setErrors((current) => ({ ...current, idCheck: undefined }));
                }}
                placeholder="아이디를 입력해주세요."
              />
              <button type="button" onClick={checkDuplicateId}>중복확인</button>
            </div>
            {idNotice ? <p className={`field-help is-${idNotice.tone}`} role="status">{idNotice.text}</p> : null}
            {errors.loginId || errors.idCheck ? <p className="field-help is-error" role="alert">{errors.loginId || errors.idCheck}</p> : null}
          </Field>
          <Field label="비밀번호">
            <input
              className={errors.password ? "has-error" : ""}
              name="newPassword"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(event) => updateForm("password", event.target.value)}
              placeholder="영문+숫자 8~20자"
              minLength={8}
              maxLength={20}
            />
            {errors.password ? <p className="field-help is-error" role="alert">{errors.password}</p> : null}
          </Field>
          <Field label="비밀번호 확인">
            <input
              className={errors.passwordConfirm ? "has-error" : ""}
              name="newPasswordConfirm"
              type="password"
              autoComplete="new-password"
              value={form.passwordConfirm}
              onChange={(event) => updateForm("passwordConfirm", event.target.value)}
              placeholder="비밀번호를 다시 입력해주세요."
              minLength={8}
              maxLength={20}
            />
            {errors.passwordConfirm ? <p className="field-help is-error" role="alert">{errors.passwordConfirm}</p> : null}
          </Field>
          <Field label="이름">
            <input
              className={errors.name ? "has-error" : ""}
              name="name"
              autoComplete="name"
              value={form.name}
              onChange={(event) => updateForm("name", event.target.value)}
              placeholder="이름을 입력해주세요."
            />
            {errors.name ? <p className="field-help is-error" role="alert">{errors.name}</p> : null}
          </Field>
          <Field label="생년월일">
            <input
              className={errors.birthDate ? "has-error" : ""}
              name="birthDate"
              type="date"
              value={form.birthDate}
              max={today}
              onChange={(event) => updateForm("birthDate", event.target.value)}
              onClick={(event) => event.currentTarget.showPicker?.()}
            />
            {errors.birthDate ? <p className="field-help is-error" role="alert">{errors.birthDate}</p> : null}
          </Field>
          <Field label="성별">
            <div className={`segmented-control${errors.gender ? " has-error" : ""}`}>
              <button className={form.gender === "female" ? "is-selected" : ""} type="button" onClick={() => updateForm("gender", "female")}>여성</button>
              <button className={form.gender === "male" ? "is-selected" : ""} type="button" onClick={() => updateForm("gender", "male")}>남성</button>
            </div>
            {errors.gender ? <p className="field-help is-error" role="alert">{errors.gender}</p> : null}
          </Field>
          <Field label="휴대폰 번호">
            <input
              className={errors.mobile ? "has-error" : ""}
              name="mobile"
              type="tel"
              autoComplete="tel"
              inputMode="numeric"
              value={form.mobile}
              onChange={(event) => updateForm("mobile", formatMobile(event.target.value))}
              placeholder="숫자만 입력해주세요."
              maxLength={13}
            />
            {errors.mobile ? <p className="field-help is-error" role="alert">{errors.mobile}</p> : null}
          </Field>
          <Field label="이메일">
            <input
              className={errors.email ? "has-error" : ""}
              name="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={form.email}
              onChange={(event) => updateForm("email", event.target.value)}
              placeholder="이메일을 입력해주세요."
            />
            {errors.email ? <p className="field-help is-error" role="alert">{errors.email}</p> : null}
          </Field>
        </div>
      </form>
      <div className="sticky-action">
        <button className="button button-primary" type="submit" form="signup-profile-form">다음</button>
      </div>
    </main>
  );
}

function formatTimer(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
  const seconds = (totalSeconds % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function EmailVerificationScreen() {
  const [verificationCode, setVerificationCode] = useState(() => getEmailVerification()?.code ?? "");
  const [pendingSignup] = useState(() => getPendingSignup());
  const [verification, setVerification] = useState(() => getEmailVerification());
  const [remainingSeconds, setRemainingSeconds] = useState(() => {
    const stored = getEmailVerification();
    return stored ? Math.max(0, Math.ceil((stored.expiresAt - Date.now()) / 1000)) : 0;
  });
  const [message, setMessage] = useState("");
  const [completed, setCompleted] = useState(false);
  const [completedName, setCompletedName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (completed || remainingSeconds <= 0) return;
    const timer = window.setInterval(() => {
      setRemainingSeconds((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [completed, remainingSeconds]);

  function resend() {
    if (!pendingSignup) {
      setMessage("회원 기본정보를 다시 입력해주세요.");
      return;
    }
    try {
      const nextVerification = issueLocalEmailVerification(pendingSignup.email);
      setVerification(nextVerification);
      setRemainingSeconds(180);
      setVerificationCode(nextVerification.code);
      setMessage("새 인증번호를 입력 영역에 반영했습니다.");
    } catch {
      setMessage("브라우저 세션 저장소를 사용할 수 없습니다.");
    }
  }

  async function verify() {
    if (!pendingSignup || !verification || verification.email !== pendingSignup.email) {
      setMessage("인증 요청 정보가 없습니다. 회원 기본정보를 다시 입력해주세요.");
      return;
    }
    if (remainingSeconds <= 0) {
      setMessage("인증시간이 만료되었습니다.");
      return;
    }
    if (!/^\d{6}$/.test(verificationCode)) {
      setMessage("인증번호 6자리를 입력해주세요.");
      return;
    }
    if (verificationCode !== verification.code) {
      setMessage("인증번호가 일치하지 않습니다.");
      return;
    }
    setSubmitting(true);
    try {
      await registerPendingUser(pendingSignup);
      setCompletedName(pendingSignup.name);
      setMessage("");
      setCompleted(true);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "회원정보를 저장할 수 없습니다.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mobile-canvas auth-flow-screen verification-screen">
      <div className="verification-base" aria-hidden={completed || undefined}>
        <AuthHeader title="회원가입" onBack={() => navigate("#/signup/profile")} />
        <section className="verification-content" aria-labelledby="verification-title">
          <h1 id="verification-title">인증번호가 전송되었습니다.<br />인증을 완료해 주세요.</h1>
          <div className="verification-field">
            <label htmlFor="verification-code">인증번호</label>
            <div className={`verification-input${message ? " has-error" : ""}`}>
              <input
                id="verification-code"
                name="verificationCode"
                value={verificationCode}
                onChange={(event) => {
                  setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6));
                  setMessage("");
                }}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                aria-describedby="verification-timer verification-message"
              />
              <span id="verification-timer" aria-live="polite">{formatTimer(remainingSeconds)}</span>
            </div>
            <p id="verification-message" className={message ? "verification-message" : "verification-message is-empty"} role="status">{message || "안내"}</p>
            <button className="verification-resend" type="button" onClick={resend}>인증번호 재발급</button>
          </div>
        </section>
        <div className="sticky-action verification-action">
          <button className="button button-primary" type="button" disabled={submitting} onClick={verify}>
            {submitting ? "확인 중..." : "확인"}
          </button>
        </div>
      </div>

      {completed ? (
        <div className="completion-layer" role="presentation">
          <section className="completion-dialog" role="dialog" aria-modal="true" aria-labelledby="completion-title" aria-describedby="completion-description">
            <div className="completion-copy">
              <h2 id="completion-title">회원가입이 완료되었습니다.</h2>
              <p id="completion-description">{completedName}님의 회원가입을 축하합니다.<br />알차고 실속있는 서비스로<br />찾아 뵙겠습니다.</p>
            </div>
            <div className="completion-action">
              <button className="button button-primary" type="button" autoFocus onClick={() => navigate("#/login")}>로그인</button>
            </div>
          </section>
        </div>
      ) : null}
    </main>
  );
}

function FindIdVerificationScreen() {
  const [verificationCode, setVerificationCode] = useState("123456");
  const [remainingSeconds, setRemainingSeconds] = useState(179);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (remainingSeconds <= 0) return;
    const timer = window.setInterval(() => {
      setRemainingSeconds((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [remainingSeconds]);

  function verify() {
    if (remainingSeconds <= 0) {
      setMessage("인증시간이 만료되었습니다.");
      return;
    }
    if (!/^\d{6}$/.test(verificationCode)) {
      setMessage("인증번호 6자리를 입력해주세요.");
      return;
    }
    setMessage("");
    navigate("#/account/find-id/result");
  }

  return (
    <main className="mobile-canvas auth-flow-screen verification-screen">
      <AuthHeader title="아이디 찾기" onBack={() => navigate("#/account/find-id")} />
      <section className="verification-content" aria-labelledby="find-id-verification-title">
        <h1 id="find-id-verification-title">인증번호가 전송되었습니다.<br />인증을 완료해 주세요.</h1>
        <div className="verification-field">
          <label htmlFor="find-id-verification-code">인증번호</label>
          <div className={`verification-input${message ? " has-error" : ""}`}>
            <input
              id="find-id-verification-code"
              name="verificationCode"
              value={verificationCode}
              onChange={(event) => {
                setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6));
                setMessage("");
              }}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              aria-describedby="find-id-verification-timer find-id-verification-message"
            />
            <span id="find-id-verification-timer" aria-live="polite">{formatTimer(remainingSeconds)}</span>
          </div>
          <p
            id="find-id-verification-message"
            className={message ? "verification-message" : "verification-message is-empty"}
            role="status"
          >
            {message || "안내"}
          </p>
        </div>
      </section>
      <div className="sticky-action verification-action">
        <button className="button button-primary" type="button" onClick={verify}>확인</button>
      </div>
    </main>
  );
}

function FindIdResultScreen() {
  return (
    <main className="mobile-canvas auth-flow-screen find-id-result-screen">
      <AuthHeader title="아이디 찾기" onBack={() => navigate("#/account/find-id/verify")} />
      <section className="find-id-result-content" aria-labelledby="find-id-result-title">
        <h1 id="find-id-result-title">
          <span>회원님의 아이디는</span>
          <strong>honggildong123</strong>
          <span>입니다.</span>
        </h1>
      </section>
      <div className="find-id-result-actions">
        <button className="button button-primary" type="button" onClick={() => navigate("#/login")}>로그인</button>
        <button className="button button-outline-primary" type="button" onClick={() => navigate("#/account/reset-password")}>
          비밀번호 찾기
        </button>
      </div>
    </main>
  );
}

function AccountRecoveryStartScreen({ mode }: { mode: "find-id" | "reset-password" }) {
  const [emailId, setEmailId] = useState("");
  const [emailDomainOption, setEmailDomainOption] = useState("");
  const [customEmailDomain, setCustomEmailDomain] = useState("");
  const isFindId = mode === "find-id";
  const title = isFindId ? "아이디 찾기" : "비밀번호 찾기";
  const emailDomain = emailDomainOption === "custom" ? customEmailDomain : emailDomainOption;
  const canSubmit = /^[^\s@]+$/.test(emailId.trim()) && /^[^\s@.]+\.[^\s@]+$/.test(emailDomain.trim());

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;
    navigate(isFindId ? "#/account/find-id/verify" : "#/account/reset-password/verify");
  }

  return (
    <main className="mobile-canvas auth-flow-screen recovery-start-screen">
      <AuthHeader title={title} onBack={() => navigate("#/login")} />
      <form className="recovery-start-form" onSubmit={submit} noValidate>
        <section className="recovery-start-content" aria-labelledby={`${mode}-start-title`}>
          <h1 id={`${mode}-start-title`}>회원가입 시 입력한<br />이메일을 인증해 주세요.</h1>
          <div className="recovery-email-field">
            <label htmlFor={`${mode}-email-id`}>이메일</label>
            <div className="recovery-email-row">
              <input
                id={`${mode}-email-id`}
                name="emailId"
                value={emailId}
                onChange={(event) => setEmailId(event.target.value.replace(/\s/g, ""))}
                inputMode="email"
                autoComplete="email"
                placeholder="아이디"
                aria-label="이메일 아이디"
              />
              <span aria-hidden="true">@</span>
              <select
                id={`${mode}-email-domain`}
                name="emailDomain"
                value={emailDomainOption}
                onChange={(event) => setEmailDomainOption(event.target.value)}
                autoComplete="email"
                aria-label="이메일 도메인"
              >
                <option value="">메일 선택</option>
                <option value="naver.com">네이버</option>
                <option value="gmail.com">지메일</option>
                <option value="daum.net">다음</option>
                <option value="custom">직접입력</option>
              </select>
            </div>
            {emailDomainOption === "custom" ? (
              <input
                className="recovery-custom-domain"
                name="customEmailDomain"
                value={customEmailDomain}
                onChange={(event) => setCustomEmailDomain(event.target.value.replace(/\s/g, ""))}
                inputMode="url"
                autoComplete="email"
                placeholder="이메일 도메인을 입력해 주세요"
                aria-label="이메일 도메인 직접 입력"
              />
            ) : null}
          </div>
        </section>
        <div className="sticky-action verification-action">
          <button className="button button-primary" type="submit" disabled={!canSubmit}>인증</button>
        </div>
      </form>
    </main>
  );
}

function ResetPasswordVerificationScreen() {
  const [verificationCode, setVerificationCode] = useState("123456");
  const [remainingSeconds, setRemainingSeconds] = useState(179);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (remainingSeconds <= 0) return;
    const timer = window.setInterval(() => {
      setRemainingSeconds((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [remainingSeconds]);

  function verify() {
    if (remainingSeconds <= 0) {
      setMessage("인증시간이 만료되었습니다.");
      return;
    }
    if (!/^\d{6}$/.test(verificationCode)) {
      setMessage("인증번호 6자리를 입력해주세요.");
      return;
    }
    setMessage("");
    navigate("#/account/reset-password/new");
  }

  return (
    <main className="mobile-canvas auth-flow-screen verification-screen">
      <AuthHeader title="비밀번호 찾기" onBack={() => navigate("#/account/reset-password")} />
      <section className="verification-content" aria-labelledby="reset-password-verification-title">
        <h1 id="reset-password-verification-title">인증번호가 전송되었습니다.<br />인증을 완료해 주세요.</h1>
        <div className="verification-field">
          <label htmlFor="reset-password-verification-code">인증번호</label>
          <div className={`verification-input${message ? " has-error" : ""}`}>
            <input
              id="reset-password-verification-code"
              name="verificationCode"
              value={verificationCode}
              onChange={(event) => {
                setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6));
                setMessage("");
              }}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              aria-describedby="reset-password-verification-timer reset-password-verification-message"
            />
            <span id="reset-password-verification-timer" aria-live="polite">{formatTimer(remainingSeconds)}</span>
          </div>
          <p
            id="reset-password-verification-message"
            className={message ? "verification-message" : "verification-message is-empty"}
            role="status"
          >
            {message || "안내"}
          </p>
        </div>
      </section>
      <div className="sticky-action verification-action">
        <button className="button button-primary" type="button" onClick={verify}>확인</button>
      </div>
    </main>
  );
}

function ResetPasswordFormScreen() {
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [message, setMessage] = useState("");

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!password || !passwordConfirm) {
      setMessage("새 비밀번호를 두 번 입력해주세요.");
      return;
    }
    if (password !== passwordConfirm) {
      setMessage("비밀번호가 일치하지 않습니다.");
      return;
    }
    setMessage("");
    navigate("#/account/reset-password/complete");
  }

  return (
    <main className="mobile-canvas auth-flow-screen password-reset-screen">
      <AuthHeader title="비밀번호 찾기" onBack={() => navigate("#/account/reset-password/verify")} />
      <form className="password-reset-form" onSubmit={submit} noValidate>
        <section className="password-reset-content" aria-labelledby="password-reset-title">
          <h1 id="password-reset-title">새로운 비밀번호를<br />입력해 주세요.</h1>
          <div className="password-reset-fields">
            <label htmlFor="new-password">비밀번호</label>
            <input
              id="new-password"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setMessage("");
              }}
              placeholder="비밀번호를 입력해 주세요"
            />
            <label className="visually-hidden" htmlFor="new-password-confirm">비밀번호 확인</label>
            <input
              id="new-password-confirm"
              name="newPasswordConfirm"
              type="password"
              autoComplete="new-password"
              value={passwordConfirm}
              onChange={(event) => {
                setPasswordConfirm(event.target.value);
                setMessage("");
              }}
              placeholder="비밀번호를 한 번 더 입력해 주세요"
            />
            <p className={message ? "password-reset-message" : "password-reset-message is-empty"} role="status">
              {message || "안내"}
            </p>
          </div>
        </section>
        <div className="sticky-action verification-action">
          <button className="button button-primary" type="submit">확인</button>
        </div>
      </form>
    </main>
  );
}

function ResetPasswordCompleteScreen() {
  return (
    <main className="mobile-canvas auth-flow-screen password-reset-complete-screen">
      <AuthHeader title="비밀번호 찾기" onBack={() => navigate("#/account/reset-password/new")} />
      <section className="password-reset-complete-content" aria-labelledby="password-reset-complete-title">
        <h1 id="password-reset-complete-title">비밀번호 설정이 완료되었습니다.<br />다시 로그인해 주세요.</h1>
      </section>
      <div className="find-id-result-actions">
        <button className="button button-primary" type="button" onClick={() => navigate("#/login")}>로그인</button>
        <button className="button button-outline-primary" type="button" onClick={() => navigate("#/account/find-id")}>
          아이디 찾기
        </button>
      </div>
    </main>
  );
}

type PlanKey = "basic" | "pro";

const planData: Record<PlanKey, {
  name: string;
  price: number;
  description: string;
  thumbnail: string;
  features: string[];
}> = {
  pro: {
    name: "Pro 구독권",
    price: 29000,
    description: "브랜드 광고 콘텐츠 제작 가능한 실무형 플랜",
    thumbnail: "/assets/art4u/figma/plan-pro.png",
    features: [
      "Basic 플랜 전체 기능 포함",
      "포즈·배경·분위기 등 모델 상세 설정",
      "상세페이지·SNS·썸네일용 결과 옵션 제공",
      "생성 이미지 보관함 및 이력 관리",
      "구독/결제/문의 관리 및 Pro 다운로드 옵션"
    ]
  },
  basic: {
    name: "Basic 구독권",
    price: 16000,
    description: "AI 광고 모델 패키지를 빠르게 제작하는 기본 플랜",
    thumbnail: "/assets/art4u/figma/plan-basic.png",
    features: [
      "상품 카테고리 및 광고 용도 선택",
      "상품 이미지·기본정보 입력",
      "기본 모델 프리셋 선택",
      "생성 결과 미리보기",
      "결과 이미지 다운로드"
    ]
  }
};

const homeProcessItems = [
  { icon: "/assets/art4u/figma/process-1.png", title: "유형 선택", description: "패션룩북, 뷰티숏폼, 증명사진" },
  { icon: "/assets/art4u/figma/process-2.png", title: "기본정보 입력", description: "기본정보 및 제품 이미지, 설명" },
  { icon: "/assets/art4u/figma/process-3.png", title: "옵션 선택", description: "비율, 구도, 배경, 포즈" },
  { icon: "/assets/art4u/figma/process-4.png", title: "모델 제작", description: "ART4U AI 모델생성" },
  { icon: "/assets/art4u/figma/process-5.png", title: "완료 및 보정", description: "결과확인 및 옵션 적용" }
];

const recommendedModels = [
  { image: "/assets/art4u/figma/model-1.jpg", name: "뷰티숏폼 | 에이", detail: "170cm / 48kg" },
  { image: "/assets/art4u/figma/model-2.jpg", name: "패션룩북 | 비", detail: "174cm / 47kg" },
  { image: "/assets/art4u/figma/model-3.jpg", name: "헤어스타일 | 비", detail: "168cm / 48kg" }
];

function BottomNavigation({ active }: { active: "home" | "models" | "ai" | "library" | "my" }) {
  const tabs: Array<{ label: string; route?: keyof typeof routeByHash }> = [
    { label: "홈", route: "#/home" },
    { label: "모델", route: "#/models" },
    { label: "AI 모델 생성", route: "#/ai-model" },
    { label: "보관함", route: "#/library" },
    { label: "마이페이지", route: "#/my" }
  ];
  const navImage = active === "models"
    ? "/assets/art4u/figma/v2/bottom-nav-model.png"
    : active === "ai"
      ? "/assets/art4u/figma/v2/bottom-nav-ai.png"
      : active === "my"
        ? "/assets/art4u/figma/v3/bottom-nav-my.png"
        : `/assets/art4u/figma/bottom-nav-${active}.png`;

  return (
    <nav className="bottom-navigation" aria-label="주요 메뉴">
      <img src={navImage} alt="" aria-hidden="true" />
      <div className="bottom-navigation-actions">
        {tabs.map((tab) => (
          <button
            key={tab.label}
            type="button"
            aria-label={tab.label}
            disabled={!tab.route}
            onClick={() => tab.route && navigate(tab.route)}
          />
        ))}
      </div>
    </nav>
  );
}

function HomeScreen() {
  const memberName = getCurrentLocalUser()?.name ?? getAuthSession()?.name ?? "회원";

  return (
    <main className="mobile-canvas app-screen home-screen">
      <header className="home-topbar">
        <img src="/assets/art4u/logo-dark.svg" alt="ART4U" />
      </header>

      <section className="home-hero">
        <img src="/assets/art4u/figma/home-symbol.png" alt="" aria-hidden="true" />
        <h1>광고 모델 없이<br />상세 페이지 컷 제작</h1>
        <p>한국형 광고 모델 샘플 패키지를<br />클릭 몇 번으로 확인하세요!</p>
        <button type="button" onClick={() => navigate("#/ai-model")}>바로 제작</button>
      </section>

      <section className="home-metrics" aria-label="ART4U 주요 수치">
        <article><span>예상 절감</span><strong>80<small>%</small></strong></article>
        <article><span>제작 시간</span><strong>5<small>분</small></strong></article>
        <article><span>샘플 컷</span><strong>1~2<small>장</small></strong></article>
      </section>

      <section className="home-process">
        <h2>ART4U의 프로세스</h2>
        <div className="home-process-card">
          {homeProcessItems.map((item) => (
            <article key={item.title}>
              <img src={item.icon} alt="" aria-hidden="true" />
              <div>
                <strong>{item.title}</strong>
                <span>{item.description}</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <button className="home-promo plan-promo" type="button" onClick={() => navigate("#/plans")}>
        <span>손쉬운 AI 모델 콘텐츠 제작</span>
        <strong><b>플랜 구독</b>을 시작해 보세요!</strong>
        <em>ART4U 플랜 둘러보기 ›</em>
        <img src="/assets/art4u/figma/plan-ticket.png" alt="" aria-hidden="true" />
      </button>

      <section className="home-models">
        <h2><b>#{memberName}</b>님에게 추천드리는 모델</h2>
        <div className="home-model-scroller">
          {recommendedModels.map((model) => (
            <article key={model.name}>
              <img src={model.image} alt="" />
              <strong>{model.name}</strong>
              <span>{model.detail}</span>
            </article>
          ))}
        </div>
        <div className="page-indicator" aria-hidden="true"><i /><i /><i /></div>
      </section>

      <button className="home-promo model-promo" type="button" onClick={() => navigate("#/models")}>
        <span>생성된 모델로 간편하게</span>
        <strong>전문 <b>AI 모델</b>을 골라보세요!</strong>
        <em>AI 모델 둘러보기 ›</em>
        <img src="/assets/art4u/figma/model-camera.png" alt="" aria-hidden="true" />
      </button>

      <section className="home-library-card">
        <div>
          <h2>최근 생성 패키지 보관함</h2>
          <p>이전 샘플 결과를 다시 확인할 수 있어요.</p>
        </div>
        <img src="/assets/art4u/figma/library-folder.png" alt="" aria-hidden="true" />
        <button type="button" onClick={() => navigate("#/library")}>확인하기</button>
      </section>
      <footer className="home-footer">
        <section>
          <strong>주식회사 피치테라피</strong>
          <p>대표이사 : 문대원</p>
          <p>서울특별시 구로구 디지털로 300, 1253호<br />(구로동, 지밸리비즈플라자)</p>
          <p>사업자등록번호 : 888-86-03605</p>
        </section>
        <section>
          <strong>고객센터 (평일 10:00 - 17:00)</strong>
          <p>연락처 : 010 - 3442 - 7247</p>
          <p>이메일 : cs@peach-therapy.com</p>
        </section>
      </footer>
      <BottomNavigation active="home" />
    </main>
  );
}

function startCheckout(plan: PlanKey) {
  window.sessionStorage.setItem("art4u:selected-plan", plan);
  navigate("#/checkout");
}

function PlanCard({ plan, onCheckout }: { plan: PlanKey; onCheckout: (plan: PlanKey) => void }) {
  const item = planData[plan];

  return (
    <article className="plan-card">
      <div className="plan-card-heading">
        <span>{item.name}</span>
        <strong>{item.price.toLocaleString("ko-KR")}원 <small>월</small></strong>
        <p>{item.description}</p>
      </div>
      <button type="button" onClick={() => onCheckout(plan)}>결제하기</button>
      <ul>
        {item.features.map((feature) => <li key={feature}>{feature}</li>)}
      </ul>
    </article>
  );
}

function PlansScreen() {
  const [pendingPlan, setPendingPlan] = useState<PlanKey | null>(null);
  const currentPayment = getCurrentLocalPayment();
  const currentPlanName = currentPayment
    ? planData[currentPayment.planKey]?.name ?? currentPayment.planName
    : "";

  function requestCheckout(plan: PlanKey) {
    if (currentPayment) {
      setPendingPlan(plan);
      return;
    }
    startCheckout(plan);
  }

  return (
    <main className="mobile-canvas app-screen plans-screen">
      <AuthHeader title="플랜" onBack={() => navigate("#/home")} />
      <section className="plans-hero">
        <img src="/assets/art4u/figma/plan-hero.jpg" alt="" />
        <div aria-hidden="true" />
        <h1>촬영 없이<br />브랜드 광고컷을 완성하세요.</h1>
        <img className="plans-hero-symbol" src="/assets/art4u/symbol.svg" alt="" aria-hidden="true" />
      </section>
      <section className="plans-content">
        <h2>플랜 업그레이드로<br />AI 모델컷 제작 기능 확장</h2>
        <PlanCard plan="pro" onCheckout={requestCheckout} />
        <PlanCard plan="basic" onCheckout={requestCheckout} />
        <p>*궁금한 사항은 고객센터로 문의 부탁드립니다.</p>
      </section>
      {pendingPlan ? (
        <div className="modal-layer" role="presentation">
          <section
            className="subscription-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="subscription-confirm-title"
          >
            <h2 id="subscription-confirm-title">추가 구독 결제</h2>
            <p>
              현재 <strong>{currentPlanName}</strong>을 이용중에 있습니다.<br />
              구독권을 추가 결제 하시겠습니까?<br />
              <small>결제된 플랜은 익월부터 적용됩니다.</small>
            </p>
            <div className="subscription-confirm-actions">
              <button type="button" onClick={() => setPendingPlan(null)}>취소</button>
              <button
                type="button"
                onClick={() => {
                  const selectedPlan = pendingPlan;
                  setPendingPlan(null);
                  startCheckout(selectedPlan);
                }}
              >
                확인
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </main>
  );
}

function CheckoutScreen() {
  const selectedPlan = (window.sessionStorage.getItem("art4u:selected-plan") === "basic" ? "basic" : "pro") as PlanKey;
  const item = planData[selectedPlan];
  const member = getCurrentLocalUser();
  const [orderNumber] = useState(() => `${Date.now()}${Math.floor(Math.random() * 1000).toString().padStart(3, "0")}`);
  const [agreements, setAgreements] = useState({ privacy: false, terms: false, payment: false });
  const [message, setMessage] = useState("");
  const [paymentCompletion, setPaymentCompletion] = useState<{ description: string } | null>(null);
  const allChecked = Object.values(agreements).every(Boolean);

  function toggleAll() {
    const next = !allChecked;
    setAgreements({ privacy: next, terms: next, payment: next });
  }

  function pay() {
    if (!member) {
      setMessage("로그인 회원 정보를 찾을 수 없습니다.");
      return;
    }
    try {
      const payment = createLocalPayment({
        orderNumber,
        memberName: member.name,
        mobile: member.mobile,
        planKey: selectedPlan,
        planName: item.name.replace("구독권", "플랜"),
        amount: item.price
      });
      const isScheduled = Date.parse(payment.effectiveAt ?? payment.paidAt) > Date.parse(payment.paidAt);
      setPaymentCompletion({
        description: isScheduled
          ? "결제가 완료되었습니다. 변경된 플랜은 익월 1일부터 적용됩니다."
          : "결제가 정상적으로 완료되었습니다."
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "결제 정보를 저장하지 못했습니다.");
    }
  }

  return (
    <main className="mobile-canvas app-screen checkout-screen">
      <AuthHeader title="결제하기" onBack={() => navigate("#/plans")} />

      <section className="checkout-section product-order-section">
        <h1>상품 정보</h1>
        <div className="checkout-product">
          <img src={item.thumbnail} alt={`${item.name} 썸네일`} />
          <div><strong>{item.name}</strong><span>{item.price.toLocaleString("ko-KR")}원</span></div>
        </div>
        <div className="checkout-notice">
          <span>[안내사항]</span>
          <p>· 교환/반품 및 환불 관련 자세한 안내는 유선 고객센터를 통해 문의해주시길 바랍니다.</p>
        </div>
      </section>

      <section className="checkout-section">
        <div className="checkout-section-title">
          <h2>주문 정보</h2>
          <span>주문번호 {orderNumber}</span>
        </div>
        <label>주문자명<input value={member?.name ?? ""} disabled /></label>
        <label>주문자 연락처<input value={member?.mobile ?? ""} disabled /></label>
      </section>

      <section className="checkout-section checkout-price">
        <h2>결제 금액</h2>
        <div><span>판매 금액</span><strong>{item.price.toLocaleString("ko-KR")}원</strong></div>
        <div><span>할인 금액</span><strong>-0원</strong></div>
        <div className="checkout-total"><span>총 결제 금액</span><strong>{item.price.toLocaleString("ko-KR")}원</strong></div>
      </section>

      <section className="checkout-section checkout-agreements">
        <h2>결제 동의</h2>
        <label className="checkout-all">
          <input type="checkbox" checked={allChecked} onChange={toggleAll} />
          <span>전체 동의</span>
        </label>
        {([
          ["privacy", "개인 정보 수집 및 이용 동의"],
          ["terms", "이용 약관 동의"],
          ["payment", "결제 동의"]
        ] as const).map(([key, label]) => (
          <label key={key}>
            <input
              type="checkbox"
              checked={agreements[key]}
              onChange={() => setAgreements((value) => ({ ...value, [key]: !value[key] }))}
            />
            <span><b>[필수]</b> {label}</span>
          </label>
        ))}
      </section>

      <div className="checkout-action">
        <button
          className="button button-primary"
          type="button"
          disabled={!allChecked || !member}
          onClick={pay}
        >
          결제하기
        </button>
        <p className={message ? "checkout-message" : "checkout-message is-empty"} role="status">{message || "안내"}</p>
      </div>
      {paymentCompletion ? (
        <div className="modal-layer" role="presentation">
          <section
            className="payment-complete-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="payment-complete-title"
            aria-describedby="payment-complete-description"
          >
            <span className="payment-complete-icon" aria-hidden="true">✓</span>
            <h2 id="payment-complete-title">결제 완료</h2>
            <p id="payment-complete-description">{paymentCompletion.description}</p>
            <button
              className="button button-primary"
              type="button"
              onClick={() => {
                setPaymentCompletion(null);
                navigate("#/my/payments/detail");
              }}
            >
              확인
            </button>
          </section>
        </div>
      ) : null}
    </main>
  );
}

function LibraryScreen() {
  const [query, setQuery] = useState("");
  const [message, setMessage] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [downloading, setDownloading] = useState(false);
  const libraryItems = getLocalCreations();
  const filteredItems = libraryItems.filter((item) =>
    `${item.type} ${item.productFileName} ${item.productDescription} ${item.optionsSummary}`.toLowerCase().includes(query.trim().toLowerCase())
  );
  const selectedItem = libraryItems.find((item) => item.id === selectedId);

  async function downloadSelectedThumbnail() {
    if (!selectedItem) {
      setMessage("다운로드할 패키지를 선택해 주세요.");
      return;
    }
    setDownloading(true);
    try {
      const response = await fetch(selectedItem.imageDataUrl);
      if (!response.ok) throw new Error("이미지를 불러오지 못했습니다.");
      const source = await response.blob();
      const jpg = new Blob([await source.arrayBuffer()], { type: "image/jpeg" });
      const url = URL.createObjectURL(jpg);
      const anchor = document.createElement("a");
      anchor.href = url;
      const baseName = selectedItem.productFileName.replace(/\.[^.]+$/, "") || selectedItem.type;
      anchor.download = `${baseName.replace(/[\\/:*?"<>|]/g, "-")}.jpg`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setMessage(`${selectedItem.productFileName} 이미지를 JPG로 다운로드했습니다.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "다운로드하지 못했습니다.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <main className="mobile-canvas app-screen library-screen">
      <header className="library-header"><h1>보관함</h1></header>
      <div className="library-search">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="검색어를 입력해주세요"
          aria-label="보관함 검색"
        />
        <span aria-hidden="true">⌕</span>
      </div>
      <p className="library-count">총 <strong>{filteredItems.length}</strong>건</p>
      <section className="library-list" aria-label="보관된 패키지">
        {filteredItems.map((item) => (
          <button
            className={selectedId === item.id ? "is-selected" : ""}
            type="button"
            key={item.id}
            aria-pressed={selectedId === item.id}
            onClick={() => {
              setSelectedId(item.id);
              setMessage(`${item.productFileName} 이미지가 선택되었습니다.`);
            }}
          >
            <img src={item.imageDataUrl} alt={`${item.productFileName} 업로드 이미지`} />
            <span>
              <strong>{item.type}</strong>
              <small>{item.productFileName} · {item.optionsSummary}</small>
              <time>{formatStoredDate(item.createdAt)}</time>
            </span>
            <i aria-hidden="true">{selectedId === item.id ? "✓" : "›"}</i>
          </button>
        ))}
        {filteredItems.length === 0 ? (
          <p className="library-empty">{query.trim() ? "검색 결과가 없습니다." : "생성 완료된 이미지가 없습니다."}</p>
        ) : null}
      </section>
      <div className="library-download">
        <button type="button" disabled={!selectedItem || downloading} onClick={downloadSelectedThumbnail}>
          {downloading ? "다운로드 중..." : "다운로드"}
        </button>
        <p className={message ? "library-message" : "library-message is-empty"} role="status">{message || "안내"}</p>
      </div>
      <BottomNavigation active="library" />
    </main>
  );
}

const v2AssetRoot = "/assets/art4u/figma/v2";

type ModelFilter = "전체" | "남성" | "여성";

const modelCatalog = [
  { image: `${v2AssetRoot}/model-01.jpg`, name: "뷰티숏폼 | 에이", detail: "170cm / 48kg", gender: "여성" },
  { image: `${v2AssetRoot}/model-02.jpg`, name: "패션룩북 | 비", detail: "174cm / 47kg", gender: "여성" },
  { image: `${v2AssetRoot}/model-03.jpg`, name: "헤어스타일 | 씨씨", detail: "168cm / 48kg", gender: "여성" },
  { image: `${v2AssetRoot}/model-04.jpg`, name: "뷰티숏폼 | 디", detail: "170cm / 48kg", gender: "여성" },
  { image: `${v2AssetRoot}/model-05.jpg`, name: "패션룩북 | 이", detail: "174cm / 47kg", gender: "여성" },
  { image: `${v2AssetRoot}/model-06.jpg`, name: "헤어스타일 | 에프", detail: "168cm / 48kg", gender: "여성" },
  { image: `${v2AssetRoot}/model-07.jpg`, name: "뷰티숏폼 | 지", detail: "168cm / 48kg", gender: "여성" },
  { image: `${v2AssetRoot}/model-08.jpg`, name: "뷰티숏폼 | 에이치", detail: "168cm / 48kg", gender: "여성" }
] as const;

type SelectedModel = {
  image: string;
  name: string;
  detail: string;
  gender: string;
};

const selectedModelKey = "art4u:selected-model";

function selectModel(model: SelectedModel) {
  window.sessionStorage.setItem(selectedModelKey, JSON.stringify(model));
  navigate("#/models/shop/images");
}

function getSelectedModel(): SelectedModel {
  const value = window.sessionStorage.getItem(selectedModelKey);
  if (value) {
    try {
      const parsed = JSON.parse(value) as Partial<SelectedModel>;
      if (parsed.image && parsed.name && parsed.detail && parsed.gender) {
        return parsed as SelectedModel;
      }
    } catch {
      window.sessionStorage.removeItem(selectedModelKey);
    }
  }
  return modelCatalog[0];
}

function ModelsScreen() {
  const [filter, setFilter] = useState<ModelFilter>("전체");
  const visibleModels = filter === "전체"
    ? [...modelCatalog].reverse()
    : [...modelCatalog].reverse().filter((model) => model.gender === filter);
  const displayCount = visibleModels.length;

  return (
    <main className="mobile-canvas app-screen models-screen">
      <header className="models-header"><h1>모델</h1></header>
      <section className="best-models">
        <h2>이 달의 BEST 모델</h2>
        <div className="best-model-scroller">
          {modelCatalog.slice(0, 3).map((model) => (
            <button type="button" key={model.name} onClick={() => selectModel(model)}>
              <img src={model.image} alt="" />
              <strong>{model.name}</strong>
              <span>{model.detail}</span>
            </button>
          ))}
        </div>
        <div className="best-model-indicator" aria-hidden="true"><i /><i /><i /></div>
      </section>
      <section className="models-catalog">
        <div className="models-catalog-heading">
          <h2>전체 <small>{displayCount}명</small></h2>
          <label>
            <span className="visually-hidden">성별 필터</span>
            <select value={filter} onChange={(event) => setFilter(event.target.value as ModelFilter)}>
              <option value="전체">전체</option>
              <option value="남성">남성</option>
              <option value="여성">여성</option>
            </select>
          </label>
        </div>
        {visibleModels.length > 0 ? (
          <div className="model-grid">
            {visibleModels.map((model) => (
              <button type="button" key={model.name} onClick={() => selectModel(model)}>
                <img src={model.image} alt="" />
                <strong>{model.name}</strong>
                <span>{model.detail}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="models-empty">등록된 남성 모델이 없습니다.</p>
        )}
      </section>
      <BottomNavigation active="models" />
    </main>
  );
}

function ShopDetailScreen({ tab }: { tab: "images" | "portfolio" }) {
  const model = getSelectedModel();
  const modelInfo = `성별 : ${model.gender}\n나이 : 22세\n혈액형 : A형\n직업 : 뷰티모델`;
  return (
    <main className="mobile-canvas app-screen shop-detail-screen">
      <header className="shop-back-header">
        <button type="button" aria-label="모델 목록으로 돌아가기" onClick={() => navigate("#/models")}>←</button>
      </header>
      <img className="shop-hero-image" src={model.image} alt={model.name} />
      <section className="shop-model-summary">
        <span>{model.detail}</span>
        <h1>{model.name}</h1>
      </section>
      <section className="shop-model-info">
        <h2>모델 정보</h2>
        <p>{modelInfo.split("\n").map((line, index) => <span key={line}>{line}{index < 3 ? <br /> : null}</span>)}</p>
      </section>
      <nav className="shop-tabs" aria-label="모델 상세 보기">
        <button className={tab === "images" ? "is-active" : ""} type="button" onClick={() => navigate("#/models/shop/images")}>추가 이미지</button>
        <button className={tab === "portfolio" ? "is-active" : ""} type="button" onClick={() => navigate("#/models/shop/portfolio")}>포트폴리오</button>
      </nav>
      <section className="shop-showcase">
        <img
          src={`${v2AssetRoot}/${tab === "images" ? "shop-additional.jpg" : "shop-portfolio.jpg"}`}
          alt={tab === "images" ? "모델 추가 이미지" : "모델 포트폴리오"}
        />
      </section>
      <div className="shop-detail-action">
        <button className="button button-primary" type="button" onClick={() => navigate("#/models/shop/apply")}>제작 신청</button>
      </div>
    </main>
  );
}

const calendarCells = [
  { day: 29, muted: true }, { day: 30, muted: true }, { day: 31, muted: true },
  ...Array.from({ length: 31 }, (_, index) => ({ day: index + 1, muted: false })),
  ...Array.from({ length: 8 }, (_, index) => ({ day: index + 1, muted: true }))
];

const consultationTimes = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
  "15:00", "15:30", "16:00", "16:30", "17:00", "17:30"
];

function ShopApplicationScreen() {
  const model = getSelectedModel();
  const [sheet, setSheet] = useState<"date" | "time" | null>(null);
  const [selectedDay, setSelectedDay] = useState(7);
  const [date, setDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [time, setTime] = useState("");
  const [complete, setComplete] = useState(false);
  const [message, setMessage] = useState("");
  const ready = Boolean(date && time);
  const modelInfo = `성별 : ${model.gender}\n나이 : 22세\n직업 : 뷰티모델`;

  function submitApplication() {
    if (!ready) return;
    setMessage("");
    try {
      createLocalModelRequest({
        modelName: model.name,
        modelImage: model.image,
        modelDetail: model.detail,
        modelInfo,
        desiredDate: date,
        desiredTime: time
      });
      setComplete(true);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "제작 신청을 저장하지 못했습니다.");
    }
  }

  return (
    <main className="mobile-canvas app-screen shop-application-screen">
      <AuthHeader title="제작 신청" onBack={() => navigate("#/models/shop/images")} />
      <section className="shop-application-content">
        <div className="shop-application-model">
          <h1>{model.name}</h1>
          <p>{modelInfo.split("\n").map((line, index) => <span key={line}>{line}{index < 2 ? <br /> : null}</span>)}</p>
        </div>
        <dl className="manager-card">
          <div><dt>담당자</dt><dd>문대원</dd></div>
          <div><dt>연락처</dt><dd>010-6277-6335</dd></div>
        </dl>
        <div className="consultation-fields">
          <label>
            상담 가능 일자
            <button type="button" onClick={() => setSheet("date")}>
              <span aria-hidden="true">▣</span>{date || "희망하는 날짜를 선택해 주세요."}<b>⌄</b>
            </button>
          </label>
          <label>
            상담 가능 시간
            <button type="button" onClick={() => setSheet("time")}>
              <span aria-hidden="true">◷</span>{time || "희망하는 시간을 선택해 주세요."}<b>⌄</b>
            </button>
          </label>
        </div>
      </section>
      <div className="shop-application-action">
        {message ? <p className="shop-application-message" role="alert">{message}</p> : null}
        <button className="button button-primary" type="button" disabled={!ready} onClick={submitApplication}>신청하기</button>
      </div>

      {sheet === "date" ? (
        <div className="sheet-layer" role="dialog" aria-modal="true" aria-label="상담 가능 일자 선택">
          <div className="bottom-sheet calendar-sheet">
            <i className="sheet-handle" aria-hidden="true" />
            <div className="calendar-heading">
              <h2>2025년 1월</h2>
              <div><button type="button" aria-label="이전 달">‹</button><button type="button" aria-label="다음 달">›</button></div>
            </div>
            <div className="calendar-weekdays" aria-hidden="true">{["일", "월", "화", "수", "목", "금", "토"].map((day) => <span key={day}>{day}</span>)}</div>
            <div className="calendar-grid">
              {calendarCells.map((cell, index) => (
                <button
                  className={`${cell.muted ? "is-muted" : ""} ${!cell.muted && cell.day === selectedDay ? "is-selected" : ""}`}
                  type="button"
                  key={`${cell.day}-${index}`}
                  disabled={cell.muted}
                  onClick={() => setSelectedDay(cell.day)}
                >
                  {cell.day}
                </button>
              ))}
            </div>
            <div className="sheet-actions">
              <button type="button" onClick={() => setSheet(null)}>취소</button>
              <button type="button" onClick={() => { setDate(`2025.01.${String(selectedDay).padStart(2, "0")}`); setSheet(null); }}>완료</button>
            </div>
          </div>
        </div>
      ) : null}

      {sheet === "time" ? (
        <div className="sheet-layer" role="dialog" aria-modal="true" aria-label="상담 가능 시간 선택">
          <div className="bottom-sheet time-sheet">
            <i className="sheet-handle" aria-hidden="true" />
            <h2>시간 선택</h2>
            <h3>오전</h3>
            <div className="time-grid">
              {consultationTimes.slice(0, 6).map((item) => (
                <button className={selectedTime === item ? "is-selected" : ""} type="button" key={item} onClick={() => setSelectedTime(item)}>{item}</button>
              ))}
            </div>
            <h3>오후</h3>
            <div className="time-grid">
              {consultationTimes.slice(6).map((item) => (
                <button className={selectedTime === item ? "is-selected" : ""} type="button" key={item} onClick={() => setSelectedTime(item)}>{item}</button>
              ))}
            </div>
            <div className="sheet-actions">
              <button type="button" onClick={() => setSheet(null)}>취소</button>
              <button type="button" disabled={!selectedTime} onClick={() => { setTime(selectedTime); setSheet(null); }}>완료</button>
            </div>
          </div>
        </div>
      ) : null}

      {complete ? (
        <div className="modal-layer" role="dialog" aria-modal="true" aria-labelledby="shop-complete-title">
          <div className="shop-complete-dialog">
            <h2 id="shop-complete-title">신청 완료</h2>
            <p>제작문의가 접수 완료되었습니다.<br />담당자 확인 후 개별 안내가 진행됩니다.</p>
            <button className="button button-primary" type="button" onClick={() => navigate("#/models/shop/images")}>확인</button>
          </div>
        </div>
      ) : null}
    </main>
  );
}

function AiStepProgress({ current }: { current: 1 | 2 | 3 }) {
  return (
    <div className="ai-step-progress" aria-label={`AI 모델 생성 ${current}단계`}>
      {[1, 2, 3].map((step) => <span className={step < current ? "is-complete" : step === current ? "is-current" : ""} key={step}>{step < current ? "✓" : step}</span>)}
    </div>
  );
}

function AiModelStartScreen() {
  return (
    <main className="mobile-canvas app-screen ai-start-screen">
      <header className="ai-start-header"><h1>AI 모델 생성</h1></header>
      <section className="ai-start-content">
        <div className="ai-start-art" aria-hidden="true">
          <span className="ai-head-back" />
          <span className="ai-head-face"><img src={`${v2AssetRoot}/ai-start-head.png`} alt="" /></span>
        </div>
        <h2>AI 광고 모델<br />무료체험</h2>
        <p>상품 정보와 원하는 모델 옵션을 선택하며<br />상세페이지에 활용할 수 있는 AI 광고 모델컷을<br />간편하게 생성해볼 수 있어요.</p>
        <article className="ai-start-card">
          <div><h3>샘플 패키지 생성</h3><span>무료체험</span></div>
          <p>AI로 상품에 어울리는 광고 모델컷을 생성합니다.</p>
          <ul>
            <li>광고 유형 선택 후 패키지 생성</li>
            <li>상품 이미지 · 설명 기반 모델컷 제작</li>
            <li>포즈 · 배경 · 스타일 옵션 선택 가능</li>
          </ul>
        </article>
      </section>
      <div className="ai-start-action">
        <button className="button button-primary" type="button" onClick={() => navigate("#/ai-model/type")}>생성 시작</button>
      </div>
      <BottomNavigation active="ai" />
    </main>
  );
}

const aiTypes = [
  { name: "패션룩북 (1P)", description: "모델 상품 패션 룩북 생성" },
  { name: "뷰티 숏폼 (1P)", description: "뷰티 모델 이미지 제작" },
  { name: "헤어스타일 (1P)", description: "헤어 모델 이미지 제작" },
  { name: "증명사진 (1P)", description: "모델 증명사진 제작" }
];

function AiTypeScreen() {
  const [selected, setSelected] = useState("");
  return (
    <main className="mobile-canvas app-screen ai-wizard-screen">
      <AuthHeader title="유형 선택" onBack={() => navigate("#/ai-model")} />
      <section className="ai-wizard-content">
        <AiStepProgress current={1} />
        <h1>원하는 광고는<br />어떤 유형인가요?</h1>
        <div className="ai-type-list">
          {aiTypes.map((item) => (
            <button className={selected === item.name ? "is-selected" : ""} type="button" key={item.name} onClick={() => setSelected(item.name)}>
              <span aria-hidden="true">✓</span>
              <div><strong>{item.name}</strong><small>{item.description}</small></div>
            </button>
          ))}
        </div>
      </section>
      <div className="wizard-action">
        <button
          className="button button-primary"
          type="button"
          disabled={!selected}
          onClick={() => {
            window.sessionStorage.setItem("art4u:ai-type", selected);
            window.sessionStorage.setItem(
              "art4u:ai-generation-id",
              "randomUUID" in window.crypto ? window.crypto.randomUUID() : `creation-${Date.now()}`
            );
            window.sessionStorage.removeItem("art4u:ai-product-image");
            window.sessionStorage.removeItem("art4u:ai-product-description");
            window.sessionStorage.removeItem("art4u:ai-options");
            navigate("#/ai-model/product");
          }}
        >
          다음
        </button>
      </div>
    </main>
  );
}

function ProductImageField({ label, required, file, onChange }: {
  label: string;
  required?: boolean;
  file: File | null;
  onChange: (file: File | null) => void;
}) {
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <div className="product-image-field">
      <span>{label}{required ? <b>*</b> : null}</span>
      <div>
        <label className="product-image-picker">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            aria-label={`${label} 이미지 선택`}
            onClick={(event) => {
              event.currentTarget.value = "";
            }}
            onChange={(event) => onChange(event.target.files?.[0] ?? null)}
          />
          <i aria-hidden="true">●</i>
          <small>({file ? 1 : 0}/3)</small>
        </label>
        {file ? (
          <div className="product-file-preview">
            <button type="button" aria-label={`${file.name} 삭제`} onClick={() => onChange(null)}>×</button>
            <img src={previewUrl} alt={`${label} 미리보기`} />
            <span>{file.name}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

async function fileToJpegThumbnail(file: File) {
  const bitmap = await createImageBitmap(file);
  try {
    const maxSide = 720;
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("이미지 변환을 지원하지 않는 브라우저입니다.");
    context.fillStyle = "#fff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.82);
  } finally {
    bitmap.close();
  }
}

function AiProductScreen() {
  const [front, setFront] = useState<File | null>(null);
  const [back, setBack] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const ready = Boolean(front && back && description.trim());

  async function continueToOptions() {
    if (!front || !ready) return;
    setSaving(true);
    setMessage("");
    try {
      const imageDataUrl = await fileToJpegThumbnail(front);
      window.sessionStorage.setItem("art4u:ai-product-image", JSON.stringify({
        imageDataUrl,
        productFileName: front.name
      }));
      window.sessionStorage.setItem("art4u:ai-product-description", description.trim());
      navigate("#/ai-model/options");
    } catch {
      setMessage("업로드 이미지를 저장하지 못했습니다. JPG, PNG 또는 WEBP 파일을 다시 선택해 주세요.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="mobile-canvas app-screen ai-wizard-screen">
      <AuthHeader title="상품 정보 입력" onBack={() => navigate("#/ai-model/type")} />
      <section className="ai-wizard-content ai-product-content">
        <AiStepProgress current={2} />
        <h1>모델 생성을 위한<br />상품 정보를 입력해주세요.</h1>
        <ProductImageField label="제품 사진(정면)" required file={front} onChange={setFront} />
        <ProductImageField label="제품 사진(후면)" file={back} onChange={setBack} />
        <label className="product-description">
          제품 설명
          <textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="제품에 대한 상세 설명을 입력해주세요." />
        </label>
      </section>
      <div className="wizard-action">
        <p className={message ? "ai-product-message" : "ai-product-message is-empty"} role="status">{message || "안내"}</p>
        <button
          className="button button-primary"
          type="button"
          disabled={!ready || saving}
          onClick={continueToOptions}
        >
          {saving ? "이미지 저장 중..." : "다음"}
        </button>
      </div>
    </main>
  );
}

type SpriteOption = {
  label: string;
  sprite: "base" | "product" | "scene";
  position: string;
};

const ratioOptions = [
  { label: "1:1", description: "프로필/썸네일" },
  { label: "4:5", description: "인스타 피드" },
  { label: "3:4", description: "광고 이미지" },
  { label: "9:16", description: "모바일" },
  { label: "16:9", description: "배너/유튜브" }
];

const compositionOptions: SpriteOption[] = [
  { label: "전신", sprite: "base", position: "0% 0%" },
  { label: "상반신", sprite: "base", position: "50% 0%" },
  { label: "클로즈업", sprite: "base", position: "100% 0%" },
  { label: "측면", sprite: "base", position: "0% 86%" },
  { label: "제품 중심", sprite: "base", position: "50% 86%" },
  { label: "모델 + 제품", sprite: "base", position: "100% 86%" }
];

const backgroundOptions: SpriteOption[] = [
  { label: "화이트 스튜디오", sprite: "product", position: "0% 0%" },
  { label: "그레이 스튜디오", sprite: "product", position: "50% 0%" },
  { label: "컬러 스튜디오", sprite: "product", position: "100% 0%" },
  { label: "모던 오피스", sprite: "product", position: "0% 86%" },
  { label: "카페", sprite: "product", position: "50% 86%" },
  { label: "도심거리", sprite: "product", position: "100% 86%" }
];

const poseOptions: SpriteOption[] = [
  { label: "정면 서기", sprite: "scene", position: "0% 0%" },
  { label: "측면 서기", sprite: "scene", position: "50% 0%" },
  { label: "앉은 자세", sprite: "scene", position: "100% 0%" },
  { label: "걷는 자세", sprite: "scene", position: "0% 86%" },
  { label: "제품 들기", sprite: "scene", position: "50% 86%" },
  { label: "자연스러운 미소", sprite: "scene", position: "100% 86%" }
];

function SpriteOptionRow({ title, options, value, onChange }: {
  title: string;
  options: SpriteOption[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <section className="ai-option-section">
      <h2>{title}</h2>
      <div className="ai-option-scroller">
        {options.map((item) => (
          <button className={value === item.label ? "is-selected" : ""} type="button" key={item.label} onClick={() => onChange(item.label)}>
            <i
              style={{
                backgroundImage: `url("${v2AssetRoot}/ai-option-${item.sprite}.jpg")`,
                backgroundPosition: item.position
              }}
              aria-hidden="true"
            />
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function AiOptionsScreen() {
  const [ratio, setRatio] = useState("");
  const [composition, setComposition] = useState("");
  const [background, setBackground] = useState("");
  const [pose, setPose] = useState("");
  const ready = Boolean(ratio && composition && background && pose);

  return (
    <main className="mobile-canvas app-screen ai-wizard-screen ai-options-screen">
      <AuthHeader title="제작 옵션 선택" onBack={() => navigate("#/ai-model/product")} />
      <section className="ai-wizard-content">
        <AiStepProgress current={3} />
        <h1>모델 생성을 위한<br />상품 정보를 입력해주세요.</h1>
        <section className="ai-option-section">
          <h2>비율</h2>
          <div className="ratio-scroller">
            {ratioOptions.map((item) => (
              <button className={ratio === item.label ? "is-selected" : ""} type="button" key={item.label} onClick={() => setRatio(item.label)}>
                <strong>{item.label}</strong><span>{item.description}</span>
              </button>
            ))}
          </div>
        </section>
        <SpriteOptionRow title="구도" options={compositionOptions} value={composition} onChange={setComposition} />
        <SpriteOptionRow title="배경" options={backgroundOptions} value={background} onChange={setBackground} />
        <SpriteOptionRow title="포즈" options={poseOptions} value={pose} onChange={setPose} />
      </section>
      <div className="wizard-action">
        <button
          className="button button-primary"
          type="button"
          disabled={!ready}
          onClick={() => {
            window.sessionStorage.setItem("art4u:ai-options", JSON.stringify({ ratio, composition, background, pose }));
            navigate("#/ai-model/loading");
          }}
        >
          생성하기
        </button>
      </div>
    </main>
  );
}

function AiLoadingScreen() {
  useEffect(() => {
    const timer = window.setTimeout(() => navigate("#/ai-model/result"), 3000);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <main className="mobile-canvas app-screen ai-loading-screen">
      <div className="ai-spinner" aria-label="AI 모델 생성 중" />
      <h1>AI가 광고 모델컷을<br />준비하고 있어요.</h1>
      <p>상품 정보와 선택한 옵션을 바탕으로<br />샘플 패키지에 맞는 모델컷을 구성하고 있어요.<br />곧 결과를 확인할 수 있어요!</p>
    </main>
  );
}

const aiStyleOptions = [
  { label: "3D 만화", image: "style-01.jpg" },
  { label: "픽셀 아트", image: "style-02.jpg" },
  { label: "CG 렌더링", image: "style-03.jpg" },
  { label: "시네마 다크", image: "style-04.jpg" },
  { label: "시네마 부다페스트", image: "style-05.jpg" },
  { label: "시네마 인터스텔라", image: "style-06.jpg" },
  { label: "8mm 빈티지", image: "style-07.jpg" }
];

function AiStyleSheet({ onCancel, onApply }: {
  onCancel: () => void;
  onApply: (style: string, strength: number) => void;
}) {
  const [style, setStyle] = useState("3D 만화");
  const [strength, setStrength] = useState(70);
  return (
    <div className="sheet-layer" role="dialog" aria-modal="true" aria-label="옵션 추가">
      <div className="bottom-sheet ai-style-sheet">
        <i className="sheet-handle" aria-hidden="true" />
        <h2>옵션 추가</h2>
        <section>
          <h3>스타일 선택</h3>
          <div className="style-scroller">
            {aiStyleOptions.map((item) => (
              <button className={style === item.label ? "is-selected" : ""} type="button" key={item.label} onClick={() => setStyle(item.label)}>
                <img src={`${v2AssetRoot}/${item.image}`} alt="" />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </section>
        <section className="strength-control">
          <h3>강조 조절</h3>
          <input type="range" min="0" max="100" value={strength} onChange={(event) => setStrength(Number(event.target.value))} />
          <div><span>0</span><strong>{strength}%</strong><span>100</span></div>
        </section>
        <div className="sheet-actions">
          <button type="button" onClick={onCancel}>취소</button>
          <button type="button" onClick={() => onApply(style, strength)}>적용</button>
        </div>
      </div>
    </div>
  );
}

type AiSavedOptions = {
  ratio: string;
  composition: string;
  background: string;
  pose: string;
};

type AiStyleEffect = {
  style: string;
  strength: number;
};

const ratioValues: Record<string, number> = {
  "1:1": 1,
  "4:5": 4 / 5,
  "3:4": 3 / 4,
  "9:16": 9 / 16,
  "16:9": 16 / 9
};

const compositionPresets: Record<string, { zoom: number; anchorX: number; anchorY: number }> = {
  "전신": { zoom: 1, anchorX: 0.5, anchorY: 0.5 },
  "상반신": { zoom: 1.16, anchorX: 0.5, anchorY: 0.4 },
  "클로즈업": { zoom: 1.38, anchorX: 0.5, anchorY: 0.34 },
  "측면": { zoom: 1.14, anchorX: 0.38, anchorY: 0.48 },
  "제품 중심": { zoom: 1.24, anchorX: 0.5, anchorY: 0.58 },
  "모델 + 제품": { zoom: 1.04, anchorX: 0.5, anchorY: 0.5 }
};

const styleFilters: Record<string, string> = {
  "3D 만화": "brightness(1.05) contrast(1.22) saturate(1.35)",
  "CG 렌더링": "brightness(1.08) contrast(1.18) saturate(1.15)",
  "시네마 다크": "brightness(0.76) contrast(1.28) saturate(0.82) hue-rotate(8deg)",
  "시네마 부다페스트": "brightness(1.08) contrast(0.92) saturate(0.86) sepia(0.14)",
  "시네마 인터스텔라": "brightness(0.9) contrast(1.22) saturate(0.72) hue-rotate(176deg)",
  "8mm 빈티지": "brightness(0.96) contrast(1.08) saturate(0.7) sepia(0.36)"
};

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}

async function renderLocalAiResult(
  imageDataUrl: string,
  options: AiSavedOptions,
  styleEffect: AiStyleEffect | null
) {
  const response = await fetch(imageDataUrl);
  if (!response.ok) throw new Error("업로드 이미지를 불러오지 못했습니다.");
  const bitmap = await createImageBitmap(await response.blob());

  try {
    const targetRatio = ratioValues[options.ratio] ?? 1;
    const sourceRatio = bitmap.width / bitmap.height;
    let cropWidth = sourceRatio > targetRatio ? bitmap.height * targetRatio : bitmap.width;
    let cropHeight = sourceRatio > targetRatio ? bitmap.height : bitmap.width / targetRatio;
    const preset = compositionPresets[options.composition] ?? compositionPresets["모델 + 제품"];

    cropWidth /= preset.zoom;
    cropHeight /= preset.zoom;

    const centerX = bitmap.width * preset.anchorX;
    const centerY = bitmap.height * preset.anchorY;
    const sourceX = clamp(centerX - cropWidth / 2, 0, bitmap.width - cropWidth);
    const sourceY = clamp(centerY - cropHeight / 2, 0, bitmap.height - cropHeight);
    const outputScale = Math.min(1, 1440 / Math.max(cropWidth, cropHeight));
    const width = Math.max(1, Math.round(cropWidth * outputScale));
    const height = Math.max(1, Math.round(cropHeight * outputScale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("이미지 가공을 지원하지 않는 브라우저입니다.");

    context.drawImage(bitmap, sourceX, sourceY, cropWidth, cropHeight, 0, 0, width, height);

    if (styleEffect && styleEffect.strength > 0) {
      const effectCanvas = document.createElement("canvas");
      effectCanvas.width = width;
      effectCanvas.height = height;
      const effectContext = effectCanvas.getContext("2d");
      if (!effectContext) throw new Error("스타일 가공을 지원하지 않는 브라우저입니다.");

      if (styleEffect.style === "픽셀 아트") {
        const pixelCanvas = document.createElement("canvas");
        const pixelSize = Math.max(4, Math.round(18 - styleEffect.strength * 0.12));
        pixelCanvas.width = Math.max(1, Math.round(width / pixelSize));
        pixelCanvas.height = Math.max(1, Math.round(height / pixelSize));
        const pixelContext = pixelCanvas.getContext("2d");
        if (!pixelContext) throw new Error("픽셀 가공을 지원하지 않는 브라우저입니다.");
        pixelContext.drawImage(canvas, 0, 0, pixelCanvas.width, pixelCanvas.height);
        effectContext.imageSmoothingEnabled = false;
        effectContext.drawImage(pixelCanvas, 0, 0, width, height);
      } else {
        effectContext.filter = styleFilters[styleEffect.style] ?? "none";
        effectContext.drawImage(canvas, 0, 0);
      }

      if (styleEffect.style === "8mm 빈티지") {
        const vignette = effectContext.createRadialGradient(
          width / 2,
          height / 2,
          Math.min(width, height) * 0.2,
          width / 2,
          height / 2,
          Math.max(width, height) * 0.72
        );
        vignette.addColorStop(0, "rgb(0 0 0 / 0)");
        vignette.addColorStop(1, "rgb(40 20 0 / 0.34)");
        effectContext.fillStyle = vignette;
        effectContext.fillRect(0, 0, width, height);
      }

      context.save();
      context.globalAlpha = clamp(styleEffect.strength / 100, 0, 1);
      context.drawImage(effectCanvas, 0, 0);
      context.restore();
    }

    return canvas.toDataURL("image/jpeg", 0.9);
  } finally {
    bitmap.close();
  }
}

function AiResultScreen() {
  const [styleSheetOpen, setStyleSheetOpen] = useState(false);
  const [styleEffect, setStyleEffect] = useState<AiStyleEffect | null>(null);
  const [resultImage, setResultImage] = useState("");
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [storageMessage, setStorageMessage] = useState("");
  const [downloadMessage, setDownloadMessage] = useState("");
  const [downloading, setDownloading] = useState(false);
  const savedOptions = useMemo<AiSavedOptions>(() => {
    const fallback = { ratio: "1:1", composition: "모델 + 제품", background: "화이트 스튜디오", pose: "제품 들기" };
    try {
      const value = window.sessionStorage.getItem("art4u:ai-options");
      return value ? { ...fallback, ...JSON.parse(value) } : fallback;
    } catch {
      return fallback;
    }
  }, []);
  const sourceProduct = useMemo(() => {
    try {
      const value = window.sessionStorage.getItem("art4u:ai-product-image");
      if (!value) return null;
      const parsed = JSON.parse(value) as { imageDataUrl?: string; productFileName?: string };
      return parsed.imageDataUrl && parsed.productFileName
        ? { imageDataUrl: parsed.imageDataUrl, productFileName: parsed.productFileName }
        : null;
    } catch {
      return null;
    }
  }, []);
  const type = (window.sessionStorage.getItem("art4u:ai-type") || "뷰티 숏폼 (1P)")
    .replace("숏폼(1P)", "숏폼 (1P)")
    .replace("헤어스타일(1P)", "헤어스타일 (1P)");
  const optionsSummary = `${savedOptions.ratio} 비율 | ${savedOptions.composition} | ${savedOptions.background} | ${savedOptions.pose}`;
  const styleSummary = styleEffect ? `${styleEffect.style} · 강조 ${styleEffect.strength}%` : "";
  const creationOptionsSummary = styleSummary ? `${optionsSummary} | ${styleSummary}` : optionsSummary;
  const ratioStyle = savedOptions.ratio.replace(":", " / ");

  useEffect(() => {
    let cancelled = false;
    if (!sourceProduct) return;

    setProcessing(true);
    setProcessingMessage("");
    setDownloadMessage("");
    renderLocalAiResult(sourceProduct.imageDataUrl, savedOptions, styleEffect)
      .then((imageDataUrl) => {
        if (!cancelled) setResultImage(imageDataUrl);
      })
      .catch((error) => {
        if (!cancelled) {
          setProcessingMessage(error instanceof Error ? error.message : "결과 이미지를 가공하지 못했습니다.");
        }
      })
      .finally(() => {
        if (!cancelled) setProcessing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    savedOptions.composition,
    savedOptions.ratio,
    sourceProduct,
    styleEffect
  ]);

  useEffect(() => {
    const generationId = window.sessionStorage.getItem("art4u:ai-generation-id");
    if (!generationId || !sourceProduct || !resultImage) return;
    try {
      upsertLocalCreation({
        id: generationId,
        type,
        productFileName: sourceProduct.productFileName,
        productDescription: window.sessionStorage.getItem("art4u:ai-product-description") ?? "",
        imageDataUrl: resultImage,
        optionsSummary: creationOptionsSummary
      });
      setStorageMessage("");
    } catch (error) {
      setStorageMessage(error instanceof Error ? error.message : "생성 기록을 저장하지 못했습니다.");
    }
  }, [creationOptionsSummary, resultImage, sourceProduct, type]);

  async function handleDownload() {
    if (!getCurrentLocalPayment()) {
      navigate("#/ai-model/download");
      return;
    }
    setDownloading(true);
    setDownloadMessage("");
    try {
      const response = await fetch(resultImage || `${v2AssetRoot}/ai-result.jpg`);
      if (!response.ok) throw new Error("결과 이미지를 불러오지 못했습니다.");
      const blob = new Blob([await response.arrayBuffer()], { type: "image/jpeg" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `art4u-ai-result-${Date.now()}.jpg`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setDownloadMessage("결과 이미지 다운로드를 시작했습니다.");
    } catch (error) {
      setDownloadMessage(error instanceof Error ? error.message : "다운로드하지 못했습니다.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <main className="mobile-canvas app-screen ai-result-screen">
      <AuthHeader title="샘플 패키지 결과" onBack={() => navigate("#/ai-model/options")} />
      <section className="ai-result-content">
        <div className="ai-result-title">
          <h1>샘플 패키지 결과</h1>
          <button type="button" disabled={downloading || processing} onClick={handleDownload}>
            {processing ? "가공 중..." : downloading ? "다운로드 중..." : "다운로드"}
          </button>
        </div>
        <p>{type}</p>
        <p>{optionsSummary}</p>
        {styleSummary ? <small>{styleSummary}</small> : null}
        {processing ? <small role="status">선택한 비율과 옵션을 이미지에 적용하고 있습니다.</small> : null}
        {processingMessage ? <small className="ai-result-storage-error">{processingMessage}</small> : null}
        {storageMessage ? <small className="ai-result-storage-error">{storageMessage}</small> : null}
        {downloadMessage ? <small className="ai-result-download-message" role="status">{downloadMessage}</small> : null}
        <img
          src={resultImage || sourceProduct?.imageDataUrl || `${v2AssetRoot}/ai-result.jpg`}
          alt="가공된 광고 이미지 결과"
          data-processed={resultImage ? "true" : "false"}
          style={{ aspectRatio: ratioStyle }}
        />
      </section>
      <div className="ai-result-actions">
        <button type="button" onClick={() => setStyleSheetOpen(true)}>옵션 추가</button>
        <button type="button" onClick={() => navigate("#/home")}>홈으로</button>
      </div>
      {styleSheetOpen ? (
        <AiStyleSheet
          onCancel={() => setStyleSheetOpen(false)}
          onApply={(style, strength) => {
            setStyleEffect({ style, strength });
            setStyleSheetOpen(false);
          }}
        />
      ) : null}
    </main>
  );
}

function DownloadPlanCard({ plan }: { plan: PlanKey }) {
  const item = planData[plan];
  return (
    <article className="download-plan-card">
      <h2>{plan === "pro" ? "Pro 플랜" : "Basic 플랜"}</h2>
      <strong>{item.price.toLocaleString("ko-KR")}원 <small>월</small></strong>
      <p>{item.description}</p>
      <button type="button" onClick={() => startCheckout(plan)}>결제하기</button>
      <ul>{item.features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
    </article>
  );
}

function AiDownloadScreen() {
  return (
    <main className="mobile-canvas app-screen ai-download-screen">
      <AuthHeader title="다운로드 안내" onBack={() => navigate("#/ai-model/result")} />
      <section className="download-content">
        <article className="download-notice">
          <h1>유료 플랜에서 다운로드 가능</h1>
          <p>유료 플랜 구독 시, 이미지 다운로드가 가능해집니다.</p>
          <ul><li>Basic : 기본 제작과 다운로드 제공</li><li>Pro : 광고 콘텐츠 패키지와 관리 기능을 제공</li></ul>
          <small>※ Basic은 가벼운 제작용 플랜입니다.<br />※ Pro는 브랜드 광고 콘텐츠 제작용 플랜입니다.</small>
        </article>
        <DownloadPlanCard plan="pro" />
        <DownloadPlanCard plan="basic" />
        <button className="download-home-button" type="button" onClick={() => navigate("#/home")}>홈으로</button>
      </section>
    </main>
  );
}

const myAssetRoot = "/assets/art4u/figma/v3";

function CompletionModal({
  title,
  description,
  buttonLabel,
  onConfirm
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  buttonLabel: string;
  onConfirm: () => void;
}) {
  return (
    <div className="modal-layer" role="presentation">
      <section className="my-completion-dialog" role="dialog" aria-modal="true" aria-labelledby="my-completion-title">
        <div>
          <h2 id="my-completion-title">{title}</h2>
          {description ? <p>{description}</p> : null}
        </div>
        <button className="button button-primary" type="button" onClick={onConfirm}>{buttonLabel}</button>
      </section>
    </div>
  );
}

function MyScreen() {
  const [modal, setModal] = useState<"logout" | "withdraw" | null>(null);
  const member = getCurrentLocalUser();
  const menuItems: Array<{ label: string; route?: keyof typeof routeByHash; action?: "logout" | "withdraw" }> = [
    { label: "내 정보", route: "#/my/profile" },
    { label: "구독 관리", route: "#/my/subscription" },
    { label: "결제 내역", route: "#/my/payments" },
    { label: "제작 요청 내역", route: "#/my/requests" },
    { label: "1:1 문의", route: "#/my/inquiries" },
    { label: "로그아웃", action: "logout" },
    { label: "회원탈퇴", action: "withdraw" }
  ];

  return (
    <main className="mobile-canvas app-screen my-screen">
      <header className="my-title-header"><h1>마이페이지</h1></header>
      <section className="my-profile-summary">
        <span className="my-avatar" aria-hidden={member?.profileImageDataUrl ? undefined : true}>
          {member?.profileImageDataUrl ? <img src={member.profileImageDataUrl} alt="프로필" /> : <i />}
        </span>
        <div>
          <strong>{member?.name ?? "회원"}님</strong>
          <small>@{member?.loginId ?? "-"}</small>
        </div>
      </section>
      <nav className="my-menu" aria-label="마이페이지 메뉴">
        {menuItems.map((item) => (
          <button
            key={item.label}
            type="button"
            onClick={() => {
              if (item.route) navigate(item.route);
              if (item.action) setModal(item.action);
            }}
          >
            <span>{item.label}</span>
            {item.route ? <i aria-hidden="true">›</i> : null}
          </button>
        ))}
      </nav>
      <BottomNavigation active="my" />

      {modal === "logout" ? (
        <CompletionModal
          title="로그아웃이 완료되었습니다."
          buttonLabel="확인"
          onConfirm={() => {
            clearAuthSession();
            navigate("#/login");
          }}
        />
      ) : null}
      {modal === "withdraw" ? (
        <CompletionModal
          title="회원탈퇴가 완료되었습니다."
          buttonLabel="확인"
          onConfirm={() => navigate("#/login")}
        />
      ) : null}
    </main>
  );
}

function MyProfileScreen() {
  const [member, setMember] = useState(() => getCurrentLocalUser());
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [phone, setPhone] = useState(() => getCurrentLocalUser()?.mobile ?? "");
  const [message, setMessage] = useState("");
  const [complete, setComplete] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState("");

  useEffect(() => () => {
    if (profileImagePreview) URL.revokeObjectURL(profileImagePreview);
  }, [profileImagePreview]);

  return (
    <main className="mobile-canvas app-screen my-profile-screen">
      <AuthHeader title="내 정보 수정" onBack={() => navigate("#/my")} />
      <form
        className="my-profile-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setMessage("");
          if (!member) {
            setMessage("로그인 회원 정보를 찾을 수 없습니다.");
            return;
          }
          if (!isValidMobile(phone)) {
            setMessage("휴대폰 번호를 정확히 입력해 주세요.");
            return;
          }
          if (password && !isValidPassword(password)) {
            setMessage("비밀번호는 영문과 숫자를 포함해 8~20자로 입력해 주세요.");
            return;
          }
          if (password !== passwordConfirm) {
            setMessage("비밀번호 확인이 일치하지 않습니다.");
            return;
          }
          try {
            const profileImageDataUrl = profileImageFile
              ? await fileToJpegThumbnail(profileImageFile)
              : member.profileImageDataUrl;
            const nextMember = await updateCurrentLocalUser({
              mobile: phone,
              password: password || undefined,
              profileImageDataUrl
            });
            setMember(nextMember);
            setPhone(nextMember.mobile);
            setPassword("");
            setPasswordConfirm("");
            setProfileImageFile(null);
            setProfileImagePreview("");
            setComplete(true);
          } catch (error) {
            setMessage(error instanceof Error ? error.message : "회원 정보를 수정하지 못했습니다.");
          }
        }}
      >
        <div className="my-profile-photo">
          <span
            className="my-avatar my-avatar-large"
            aria-hidden={profileImagePreview || member?.profileImageDataUrl ? undefined : true}
          >
            {profileImagePreview || member?.profileImageDataUrl ? (
              <img src={profileImagePreview || member?.profileImageDataUrl} alt="선택한 프로필" />
            ) : <i />}
          </span>
          <label>
            프로필 변경
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              aria-label="프로필 이미지 선택"
              onClick={(event) => {
                event.currentTarget.value = "";
              }}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                setProfileImageFile(file);
                setProfileImagePreview(URL.createObjectURL(file));
              }}
            />
          </label>
        </div>

        <section className="my-form-section">
          <h1>정보 입력</h1>
          <label>
            <span>이름</span>
            <input value={member?.name ?? ""} disabled />
            <small>*이름은 수정이 불가능합니다.</small>
          </label>
          <label>
            <span>ID</span>
            <input value={member?.loginId ?? ""} disabled />
            <small>*ID는 수정이 불가능합니다.</small>
          </label>
          <label>
            <span>비밀번호</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="비밀번호를 입력해 주세요."
              autoComplete="new-password"
            />
          </label>
          <label>
            <span>비밀번호 확인</span>
            <input
              type="password"
              value={passwordConfirm}
              onChange={(event) => setPasswordConfirm(event.target.value)}
              placeholder="비밀번호를 다시 입력해 주세요."
              autoComplete="new-password"
            />
          </label>
          <label>
            <span>휴대폰 번호</span>
            <input
              inputMode="tel"
              value={phone}
              onChange={(event) => {
                setPhone(formatMobile(event.target.value));
                setMessage("");
              }}
            />
          </label>
          <label>
            <span>이메일</span>
            <input type="email" value={member?.email ?? ""} disabled />
            <small>*이메일 변경은 고객센터로 문의해 주세요.</small>
          </label>
          <p className={message ? "my-profile-message" : "my-profile-message is-empty"} role="status">
            {message || "안내"}
          </p>
        </section>
        <div className="my-fixed-action">
          <button className="button button-primary" type="submit">수정하기</button>
        </div>
      </form>

      {complete ? (
        <CompletionModal
          title={<>내 정보 수정이<br />완료되었습니다.</>}
          description={<>확인 버튼을 누르시면<br />정보 입력 페이지로 이동합니다.</>}
          buttonLabel="확인"
          onConfirm={() => setComplete(false)}
        />
      ) : null}
    </main>
  );
}

function MySubscriptionScreen() {
  const currentPayment = getCurrentLocalPayment();
  const pendingPayment = getPendingLocalPayment();
  const currentPlan = currentPayment ? planData[currentPayment.planKey] : null;
  const pendingPlan = pendingPayment ? planData[pendingPayment.planKey] : null;

  return (
    <main className="mobile-canvas app-screen my-subscription-screen">
      <AuthHeader title="구독관리" onBack={() => navigate("#/my")} />
      <section className="subscription-content">
        <h1>현재 이용 중인 플랜</h1>
        {currentPayment && currentPlan ? (
          <article className="current-plan-card">
            <div className="current-plan-heading">
              <span>{currentPlan.name}</span>
              <em>이용중</em>
            </div>
            <strong>{currentPayment.amount.toLocaleString("ko-KR")}원<small> 월</small></strong>
            <p>{currentPlan.description}</p>
            <ul>{currentPlan.features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
          </article>
        ) : (
          <p className="subscription-empty">현재 이용 중인 플랜이 없습니다.</p>
        )}
        {pendingPayment && pendingPlan ? (
          <p className="subscription-change-notice">
            <strong>{formatStoredDate(pendingPayment.effectiveAt ?? pendingPayment.paidAt)}</strong>부터{" "}
            <strong>{pendingPlan.name}</strong>으로 변경됩니다.
          </p>
        ) : null}
        <h2>이번 달 사용량</h2>
        <article className="usage-card">
          <div><span>샘플 패키지 생성</span><strong>0회</strong></div>
          <div className="usage-progress" aria-label="이번 달 저장된 생성 내역 없음"><i style={{ width: 0 }} /></div>
        </article>
      </section>
      <div className="subscription-actions">
        <button type="button" onClick={() => navigate("#/plans")}>플랜 변경</button>
        <button type="button" onClick={() => navigate("#/my/payments")}>결제 내역</button>
      </div>
    </main>
  );
}

function RequestSummary({
  request,
  showRequestNumber = true
}: {
  request: LocalModelRequest;
  showRequestNumber?: boolean;
}) {
  return (
    <>
      <strong className="my-status">{request.status}</strong>
      <div className="my-product-row">
        <img src={request.modelImage} alt={request.modelName} />
        <div>
          {showRequestNumber ? <small>요청번호 {request.id.slice(-8).toUpperCase()}</small> : null}
          <span>{request.modelName}</span>
          <time>{request.desiredDate} {request.desiredTime}</time>
        </div>
      </div>
    </>
  );
}

function MyRequestsScreen() {
  const productionRequests = getLocalModelRequests();
  return (
    <main className="mobile-canvas app-screen my-list-screen">
      <AuthHeader title="제작 요청 내역" onBack={() => navigate("#/my")} />
      <section className="history-list" aria-label="제작 요청 목록">
        {productionRequests.map((request) => (
          <article key={request.id}>
            <div className="history-list-heading">
              <time>{formatStoredDate(request.createdAt)}</time>
              <button
                type="button"
                onClick={() => {
                  selectLocalModelRequest(request.id);
                  navigate("#/my/requests/detail");
                }}
              >
                상세보기
              </button>
            </div>
            <RequestSummary request={request} />
          </article>
        ))}
        {productionRequests.length === 0 ? <p className="history-empty">제작 요청 내역이 없습니다.</p> : null}
      </section>
    </main>
  );
}

function DetailRows({ rows }: { rows: Array<[string, string]> }) {
  return (
    <dl className="detail-rows">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function DetailAccordion({
  title,
  defaultOpen = true,
  children
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`detail-accordion${open ? " is-open" : ""}`}>
      <button type="button" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <strong>{title}</strong><span aria-hidden="true">⌃</span>
      </button>
      {open ? <div>{children}</div> : null}
    </section>
  );
}

function MyRequestDetailScreen() {
  const request = getSelectedLocalModelRequest();
  const member = getCurrentLocalUser();
  return (
    <main className="mobile-canvas app-screen my-detail-screen">
      <AuthHeader title="제작 요청 상세" onBack={() => navigate("#/my/requests")} />
      {request ? (
        <>
          <section className="request-detail-summary">
            <h1>제작 요청 정보</h1>
            <RequestSummary request={request} showRequestNumber={false} />
            <DetailRows rows={[
              ["회원명", member?.name ?? "-"],
              ["회원 연락처", member?.mobile ?? "-"]
            ]} />
          </section>
          <DetailAccordion title="신청 모델 정보">
            <DetailRows rows={[
              ["모델명", request.modelName],
              ["신체 정보", request.modelDetail],
              ["모델 정보", request.modelInfo.replace(/\n/g, " / ")]
            ]} />
          </DetailAccordion>
          <DetailAccordion title="상담 희망 일정">
            <DetailRows rows={[
              ["희망 일자", request.desiredDate],
              ["희망 시간", request.desiredTime],
              ["신청 일시", formatStoredDateTime(request.createdAt)]
            ]} />
          </DetailAccordion>
        </>
      ) : (
        <p className="history-empty">확인할 제작 요청을 선택해 주세요.</p>
      )}
    </main>
  );
}

function formatStoredDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const pad = (number: number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())}`;
}

function formatStoredDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const pad = (number: number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function PaymentSummary({ payment, showNumber = true }: { payment: LocalPayment; showNumber?: boolean }) {
  return (
    <>
      <strong className="my-status">{payment.status}</strong>
      <div className="my-product-row payment-product-row">
        <img src={`${myAssetRoot}/plan-${payment.planKey}.png`} alt={payment.planName} />
        <div>
          {showNumber ? <small>결제번호 {payment.orderNumber}</small> : null}
          <span>{payment.planName}</span>
          <time>{payment.amount.toLocaleString("ko-KR")}원</time>
        </div>
      </div>
    </>
  );
}

function MyPaymentsScreen() {
  const payments = getLocalPayments();
  return (
    <main className="mobile-canvas app-screen my-list-screen">
      <AuthHeader title="결제 내역" onBack={() => navigate("#/my")} />
      <section className="history-list" aria-label="결제 내역">
        {payments.map((payment) => (
          <article key={payment.id}>
            <div className="history-list-heading">
              <time>{formatStoredDate(payment.paidAt)}</time>
              <button
                type="button"
                onClick={() => {
                  selectLocalPayment(payment.id);
                  navigate("#/my/payments/detail");
                }}
              >
                상세보기
              </button>
            </div>
            <PaymentSummary payment={payment} />
          </article>
        ))}
        {payments.length === 0 ? <p className="history-empty">저장된 결제 내역이 없습니다.</p> : null}
      </section>
    </main>
  );
}

function MyPaymentDetailScreen() {
  const payment = getSelectedLocalPayment();
  return (
    <main className="mobile-canvas app-screen my-detail-screen">
      <AuthHeader title="결제 내역 상세" onBack={() => navigate("#/my/payments")} />
      {payment ? (
        <>
          <section className="request-detail-summary payment-detail-summary">
            <h1>주문 정보</h1>
            <PaymentSummary payment={payment} />
            <DetailRows rows={[["회원명", payment.memberName], ["회원 연락처", payment.mobile]]} />
          </section>
          <DetailAccordion title="플랜 정보" defaultOpen={false}>
            <DetailRows rows={[
              ["상품명", payment.planName],
              ["판매가", `${payment.amount.toLocaleString("ko-KR")}원`]
            ]} />
          </DetailAccordion>
          <DetailAccordion title="결제 정보" defaultOpen={false}>
            <DetailRows rows={[
              ["결제 번호", payment.orderNumber],
              ["결제 상태", payment.status],
              ["결제 수단", payment.paymentMethod],
              ["결제 일시", formatStoredDateTime(payment.paidAt)],
              ["플랜 적용일", formatStoredDate(payment.effectiveAt ?? payment.paidAt)]
            ]} />
            <div className="payment-total">
              <span>최종 결제 금액</span>
              <strong>{payment.amount.toLocaleString("ko-KR")}원</strong>
            </div>
          </DetailAccordion>
        </>
      ) : (
        <p className="history-empty">확인할 결제 내역을 선택해 주세요.</p>
      )}
    </main>
  );
}

const inquiryCategories = ["전체", "서비스 이용 문의", "탈퇴 문의", "환불 문의", "제휴 문의", "신고"] as const;
type InquiryCategory = typeof inquiryCategories[number];

function MyInquiriesScreen() {
  const [filter, setFilter] = useState<InquiryCategory>("전체");
  const inquiries = getLocalInquiries();
  const visibleInquiries = filter === "전체"
    ? inquiries
    : inquiries.filter((inquiry) => inquiry.category === filter);

  return (
    <main className="mobile-canvas app-screen inquiry-list-screen">
      <AuthHeader title="1:1 문의 내역" onBack={() => navigate("#/my")} />
      <section className="inquiry-list-heading">
        <h1>문의 <strong>{visibleInquiries.length}</strong></h1>
        <div className="inquiry-filters" role="tablist" aria-label="문의 구분">
          {inquiryCategories.map((category) => (
            <button
              key={category}
              className={category === filter ? "is-active" : ""}
              type="button"
              role="tab"
              aria-selected={category === filter}
              onClick={() => setFilter(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </section>
      <section className="inquiry-items" aria-label="문의 목록">
        {visibleInquiries.map((inquiry) => (
          <button
            type="button"
            key={inquiry.id}
            onClick={() => {
              selectLocalInquiry(inquiry.id);
              navigate("#/my/inquiries/detail");
            }}
          >
            <small>{inquiry.status}</small>
            <strong>{inquiry.title}</strong>
            <time>{formatStoredDate(inquiry.createdAt)}</time>
          </button>
        ))}
        {visibleInquiries.length === 0 ? <p className="history-empty">등록된 문의가 없습니다.</p> : null}
      </section>
      <div className="my-fixed-action">
        <button className="button button-primary" type="button" onClick={() => navigate("#/my/inquiries/new")}>문의하기</button>
      </div>
    </main>
  );
}

function MyInquiryNewScreen() {
  const [category, setCategory] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [selectOpen, setSelectOpen] = useState(false);
  const [complete, setComplete] = useState(false);
  const [message, setMessage] = useState("");
  const inquiryOptions = inquiryCategories.filter((item) => item !== "전체");
  const canSubmit = Boolean(category && title.trim() && content.trim());

  return (
    <main className="mobile-canvas app-screen inquiry-form-screen">
      <AuthHeader title="문의하기" onBack={() => navigate("#/my/inquiries")} />
      <form
        className="inquiry-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          try {
            createLocalInquiry({
              category,
              title: title.trim(),
              content: content.trim()
            });
            setComplete(true);
          } catch (error) {
            setMessage(error instanceof Error ? error.message : "문의를 저장하지 못했습니다.");
          }
        }}
      >
        <label className="inquiry-select-field">
          <span>문의 구분</span>
          <button
            className={selectOpen ? "is-open" : ""}
            type="button"
            aria-haspopup="listbox"
            aria-expanded={selectOpen}
            onClick={() => setSelectOpen((value) => !value)}
          >
            <span className={category ? "has-value" : ""}>{category || "문의 유형을 선택해 주세요."}</span>
            <i aria-hidden="true">⌄</i>
          </button>
          {selectOpen ? (
            <div className="inquiry-select-options" role="listbox">
              {inquiryOptions.map((option) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={category === option}
                  key={option}
                  onClick={() => {
                    setCategory(option);
                    setSelectOpen(false);
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          ) : null}
        </label>
        <label>
          <span>문의 제목</span>
          <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="문의 제목을 작성해 주세요." />
        </label>
        <label>
          <span>문의 내용</span>
          <textarea value={content} onChange={(event) => setContent(event.target.value)} placeholder="문의하실 내용을 입력해 주세요." />
        </label>
        <p className={message ? "inquiry-form-message" : "inquiry-form-message is-empty"} role="status">
          {message || "안내"}
        </p>
        <div className="my-fixed-action">
          <button className="button button-primary" type="submit" disabled={!canSubmit}>완료</button>
        </div>
      </form>

      {complete ? (
        <CompletionModal
          title="문의가 완료되었습니다."
          description={<>마이페이지 &gt; 문의내역에서<br />문의 정보를 확인하실 수 있습니다.</>}
          buttonLabel="확인"
          onConfirm={() => navigate("#/my/inquiries")}
        />
      ) : null}
    </main>
  );
}

function MyInquiryDetailScreen() {
  const inquiry = getSelectedLocalInquiry();
  return (
    <main className="mobile-canvas app-screen inquiry-detail-screen">
      <AuthHeader title="문의 상세" onBack={() => navigate("#/my/inquiries")} />
      {inquiry ? (
        <>
          <section className="inquiry-question">
            <small>[{inquiry.category}]</small>
            <h1>{inquiry.title}</h1>
            <p>{inquiry.content}</p>
            <time>{formatStoredDate(inquiry.createdAt)}</time>
          </section>
          <section className="inquiry-answer">
            <small>[{inquiry.status}]</small>
            {inquiry.answer ? (
              <>
                <h2>‘{inquiry.title}’의 답변입니다.</h2>
                <p>{inquiry.answer}</p>
                <time>{inquiry.answeredAt ? formatStoredDate(inquiry.answeredAt) : ""}</time>
              </>
            ) : (
              <>
                <h2>답변을 준비하고 있습니다.</h2>
                <p>문의 내용을 확인한 뒤 답변을 등록해 드리겠습니다.</p>
              </>
            )}
          </section>
        </>
      ) : (
        <p className="history-empty">확인할 문의 내역을 선택해 주세요.</p>
      )}
    </main>
  );
}

function App() {
  const [route, setRoute] = useState<Route>(() => {
    if (!window.location.hash && window.sessionStorage.getItem(splashSeenKey) === "true") {
      return "login";
    }
    return currentRoute();
  });

  useEffect(() => {
    if (!window.location.hash) {
      const initialHash = window.sessionStorage.getItem(splashSeenKey) === "true" ? "#/login" : "#/splash";
      window.history.replaceState(null, "", initialHash);
      setRoute(currentRoute());
    }
    const onHashChange = () => setRoute(currentRoute());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [route]);

  return useMemo(() => {
    if (route === "login") return <LoginScreen />;
    if (route === "signup-terms") return <TermsScreen />;
    if (route === "signup-profile") return <ProfileScreen />;
    if (route === "signup-verify-email") return <EmailVerificationScreen />;
    if (route === "find-id-start") return <AccountRecoveryStartScreen mode="find-id" />;
    if (route === "find-id-verify") return <FindIdVerificationScreen />;
    if (route === "find-id-result") return <FindIdResultScreen />;
    if (route === "reset-password-start") return <AccountRecoveryStartScreen mode="reset-password" />;
    if (route === "reset-password-verify") return <ResetPasswordVerificationScreen />;
    if (route === "reset-password-form") return <ResetPasswordFormScreen />;
    if (route === "reset-password-complete") return <ResetPasswordCompleteScreen />;
    if (route === "home") return <HomeScreen />;
    if (route === "plans") return <PlansScreen />;
    if (route === "checkout") return <CheckoutScreen />;
    if (route === "library") return <LibraryScreen />;
    if (route === "models") return <ModelsScreen />;
    if (route === "shop-images") return <ShopDetailScreen tab="images" />;
    if (route === "shop-portfolio") return <ShopDetailScreen tab="portfolio" />;
    if (route === "shop-apply") return <ShopApplicationScreen />;
    if (route === "ai-model") return <AiModelStartScreen />;
    if (route === "ai-type") return <AiTypeScreen />;
    if (route === "ai-product") return <AiProductScreen />;
    if (route === "ai-options") return <AiOptionsScreen />;
    if (route === "ai-loading") return <AiLoadingScreen />;
    if (route === "ai-result") return <AiResultScreen />;
    if (route === "ai-download") return <AiDownloadScreen />;
    if (route === "my") return <MyScreen />;
    if (route === "my-profile") return <MyProfileScreen />;
    if (route === "my-subscription") return <MySubscriptionScreen />;
    if (route === "my-requests") return <MyRequestsScreen />;
    if (route === "my-request-detail") return <MyRequestDetailScreen />;
    if (route === "my-payments") return <MyPaymentsScreen />;
    if (route === "my-payment-detail") return <MyPaymentDetailScreen />;
    if (route === "my-inquiries") return <MyInquiriesScreen />;
    if (route === "my-inquiry-new") return <MyInquiryNewScreen />;
    if (route === "my-inquiry-detail") return <MyInquiryDetailScreen />;
    return <SplashScreen />;
  }, [route]);
}

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => navigator.serviceWorker.register("/sw.js").catch(() => undefined));
}

createRoot(document.getElementById("root")!).render(<StrictMode><App /></StrictMode>);
